import React, { useEffect, useState, useCallback } from "react";
import Layout from "../../Component/Layout";
import Select from "react-select";
import axios from "../../http"; // attaches the login token (was plain "axios")
import { toast, ToastContainer } from "react-toastify";
import { MASTER_API } from "../../apiConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import sbeeLogo from "../../assets/sbeelogo.png";

/* ── Source options for the characteristics table ── */
const SOURCE_OPTIONS = [
  { value: "Manual",         label: "Manual"         },
  { value: "Product Master", label: "Product Master" },
  { value: "QA-FG",          label: "QA-FG"          },
  { value: "Routing",        label: "Routing"        },
  { value: "TBA-GTP",        label: "TBA-GTP"        },
];

/* ── GTP Type options (multi-select, checkbox-style) ── */
const GTP_TYPE_OPTIONS = [
  { value: "Sales",  label: "Sales"  },
  { value: "Master", label: "Master" },
];

/* Custom react-select Option renderer that shows a checkbox next to each
   option, so a multi-select reads like a checkbox list. */
const CheckboxOption = (props) => {
  const { innerProps, innerRef, isSelected, label } = props;
  return (
    <div ref={innerRef} {...innerProps} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
      <input type="checkbox" checked={isSelected} onChange={() => {}} style={{ margin: 0 }} />
      <span>{label}</span>
    </div>
  );
};

/* ── Explicit characteristic → column aliases for QA-FG (`gtp qafg`) ──
   These pairs are named too differently for fuzzy substring matching to
   find reliably, so they're mapped exactly. Key = normalize(characteristic
   label as configured in Quality Configuration), Value = qafg column name. */
const QAFG_ALIASES = {
  [normalizeKey("Material & Purity of Conductor")]: "material",
  [normalizeKey("Form of Conductor")]: "form_of_conductor_circular_shaped",
  [normalizeKey("Number of strands")]: "number_of_strands",
  [normalizeKey("Nominal conductor diameter")]: "bunched_dia",
  [normalizeKey("Maximum DC Resistance of the conductor per KM at 20 Deg C.")]: "maximum_dc_resistance_of_the_conductor_at_20c",
  [normalizeKey("Lay Ratio")]: "measurement_of_lay_ratio",
  [normalizeKey("Maximum Elongation under load in process")]: "hot_set_test_for_xlpe_elongation_under_load",
  [normalizeKey("Maximum Permanent elongation after cooling")]: "hot_set_test_for_xlpe_permanent_elongation_after_coolin",
  [normalizeKey("Minimum Tensile Strength at break")]: "tensile_strength_before_aging_for_insulation",
  [normalizeKey("Shrinkage Test")]: "shrinkage_test_for_insulation",
  [normalizeKey("Water Absorption test : Water absorbed")]: "water_absorption_test",
  [normalizeKey("Carbon Black Content Shall be ranging")]: "carbon_black_content_test_for_insulation",
  [normalizeKey("Armour Resistivity (Max) as per IS 3975")]: "armour_resistivity_test",
  // "Mass of Zinc coating (for GI)" intentionally omitted — no matching column
  // exists in gtp qafg (only uniformity_of_zinc_coating_test, a different test).
  [normalizeKey("Max DC resistance of armour")]: "armour_resistance_at_20_degree_celsius",
  [normalizeKey("Tensile Strength at break (For Aluminium Armour)")]: "armour_tensile_strength_test",
  [normalizeKey("Minimum thermal stability test for PVC sheath")]: "thermal_stability_test_for_pvc_for_outer_sheath",
  [normalizeKey("Insulation resistance at 27 C")]: "insulation_resistance_at_room_temperature",
  [normalizeKey("Volume resistivity of Insulation Minimum at 27C")]: "volume_resistivity_at_room_temperature",
  [normalizeKey("Oxygen Index Test ( ASTM D2863-1987)")]: "oxygen_index_test_astm_d_2863_for_insulation",
  [normalizeKey("Temperature Index ( ASTM D2863-1987)")]: "temperature_index_test_astm_d_2863_for_insulation",
  [normalizeKey("Halogen Acid Geeneration Test (IEC 60754-1)")]: "halogen_acid_generation_test_corrosive_acid_gas_test_ie",
  [normalizeKey("Smoke Density Test as per ASTM D2843-13")]: "smoke_density_test_astm_d2843_for_insulation",
  [normalizeKey("pH Test")]: "ph_test_for_insulation",
  [normalizeKey("Conductivity Test")]: "conductivity_test_for_insulation",
  [normalizeKey("CWZ Test (BS 6387 /BS 8491/ IEC 60331-P1 and P2)")]: "cwz_test_std",
};

/* ── Explicit characteristic → column aliases for Routing (`gtp routing`) ──
   IMPORTANT: several characteristic labels (e.g. "Material", "Type of
   Extrusion", "Colour of Outer Sheath") are reused across multiple
   Processes but map to DIFFERENT columns depending on which Process they
   belong to. A plain characteristic-only key can't tell them apart, so
   these are keyed by `${process}||${characteristic}` via aliasKey(). Keys
   below are grouped by Process to match the Quality Configuration sheet. */
const ROUTING_ALIASES = {
  // Armouring
  [aliasKey("Armouring", "Material")]: "armouring_material",
  [aliasKey("Armouring", "Minimum % Coverage")]: "min_percent_coverage",
  [aliasKey("Armouring", "Nominal dia over Armouring")]: "nominal_dia_over_armouring",
  [aliasKey("Armouring", "Nominal Dimension of Armouring Wire /Strip /Tape")]: "nominal_dimension_of_armouring_wire_strip_tape",

  // BRADING (braiding)
  [aliasKey("BRADING", "% of coverage")]: "braiding_percent_coverage",
  [aliasKey("BRADING", "d) Diameter over Braiding")]: "diameter_over_braiding",
  [aliasKey("BRADING", "Material")]: "braiding_material_mention_type",

  // Coductor Screening
  [aliasKey("Coductor Screening", "Thikness of Screening")]: "thickness_of_screening",

  // Individual Pair Screening
  [aliasKey("Individual Pair Screening", "% of Overlap of Aluminium Mylar tape")]: "aluminium_mylar_percent_overlap_1",
  [aliasKey("Individual Pair Screening", "% of Overlap of Polyester Tape")]: "polyester_tape_percent_overlap_1",
  [aliasKey("Individual Pair Screening", "Drain Wire (No. of Wire and each strand dia in mm)")]: "drain_wire_no_and_strand_dia_1",
  [aliasKey("Individual Pair Screening", "Dia over Individual pair Screening")]: "dia_over_individual_pair_screening",

  // Inner Sheathing
  [aliasKey("Inner Sheathing", "Material(mention Type)")]: "material_mention_type",
  [aliasKey("Inner Sheathing", "Type of Extrusion")]: "type_of_extrusion",
  [aliasKey("Inner Sheathing", "Colour of Inner Sheath")]: "colour_of_inner_sheath",
  [aliasKey("Inner Sheathing", "Calculated diameter over laid up cores")]: "calculated_diameter_over_laid_up_cores",
  [aliasKey("Inner Sheathing", "Minimum Thickness Of Inner Sheath(mm)")]: "min_thickness_of_inner_sheath_mm",
  [aliasKey("Inner Sheathing", "Nominal dia over Inner sheath")]: "nominal_dia_over_inner_sheath",

  // Insulation Screening
  [aliasKey("Insulation Screening", "% of Overlap")]: "percent_of_overlap",
  [aliasKey("Insulation Screening", "Combined Nominal thickness of Coverings (Both layers of Insulation & Outer covering) together")]: "combined_nominal_thickness_of_coverings",
  [aliasKey("Insulation Screening", "Identification tape")]: "identification_tape",
  [aliasKey("Insulation Screening", "Material (metallic part)")]: "material_metallic_part",
  [aliasKey("Insulation Screening", "Material (Non metallic part)")]: "material_non_metallic_part",
  [aliasKey("Insulation Screening", "Size of metallic tape")]: "size_of_metallic_tape",
  [aliasKey("Insulation Screening", "Thikness of Screening")]: "thickness_of_screening_2",
  [aliasKey("Insulation Screening", "Thikness Water swellable Tape")]: "thickness_water_swellable_tape",

  // Outer Sheathing
  [aliasKey("Outer Sheathing", "Type of Extrusion")]: "outer_sheath_type_of_extrusion",
  [aliasKey("Outer Sheathing", "Material")]: "outer_sheath_material",
  [aliasKey("Outer Sheathing", "Min. thickness of sheath")]: "min_thickness_of_sheath",
  [aliasKey("Outer Sheathing", "Nominal thickness of Sheathing")]: "nominal_thickness_of_sheathing",
  [aliasKey("Outer Sheathing", "Nominal Over all Diameter of cable")]: "nominal_overall_diameter_of_cable",
  [aliasKey("Outer Sheathing", "Colour of Outer Sheath")]: "colour_of_outer_sheath_final",

  // Overall Screening
  [aliasKey("Overall Screening", "Dia over Overall Screening")]: "dia_over_overall_screening",
  [aliasKey("Overall Screening", "Drain Wire (No. of Wire and each strand dia in mm)")]: "drain_wire_no_and_strand_dia_2",
  [aliasKey("Overall Screening", "% of Overlap of Aluminium Mylar tape")]: "aluminium_mylar_percent_overlap_2",
  [aliasKey("Overall Screening", "% of Overlap of Polyester Tape")]: "polyester_tape_percent_overlap_2",

  // Polyethylene Sheath Over Core
  [aliasKey("Polyethylene Sheath Over Core", "Colour of Outer sheath")]: "colour_of_outer_sheath",
  [aliasKey("Insulation", "Colour Scheme Of Identification Of Cores")]: "colour_scheme_identification_of_cores",
  [aliasKey("Polyethylene Sheath Over Core", "Material")]: "material",
  [aliasKey("Polyethylene Sheath Over Core", "Nominal thickness")]: "nominal_thickness",

  // PRINITING/ Embossing
  [aliasKey("PRINITING/ Embossing", "PRINITING/ Embossing")]: "printing_embossing",
};

/* ── Explicit characteristic → column aliases for TBA-GTP (`gtp_details`) ── */
const TBA_GTP_ALIASES = {
  [aliasKey("Armouring", "Short circuit rating in 1 sec of armour")]: "short_circuit_rating_1sec_armour",

  [aliasKey("BRADING", "Aluminium Mylar tape Thikness")]: "aluminium_mylar_tape_thickness",
  [aliasKey("BRADING", "Polyester Tape thikness over braiding")]: "polyester_tape_thickness_over_braiding",

  [aliasKey("CABLE", "Suitable for effective Earth/Unearth system")]: "suitable_for_effective_earth_unearth_system",

  [aliasKey("Coductor Screening", "Extrusion process")]: "extrusion_process",

  [aliasKey("CONDUCTOR", "Applicable Standard")]: "applicable_standard",
  [aliasKey("CONDUCTOR", "Resistivity of Conductor")]: "resistivity_of_conductor",
  [aliasKey("CONDUCTOR", "Class of Conductor")]: "class_of_conductor",
  [aliasKey("CONDUCTOR", "Minimum Tensile Strength of Conductor")]: "min_tensile_strength_of_conductor",

  [aliasKey("Continuous current for standard condition as per IS", "In air (45ºC)")]: "current_carrying_capacity_in_air_45c",
  [aliasKey("Continuous current for standard condition as per IS", "In Duct")]: "current_carrying_capacity_in_duct",
  [aliasKey("Continuous current for standard condition as per IS", "In Ground (30ºC)")]: "current_carrying_capacity_in_ground_30c",

  [aliasKey("Hot Set test (Inprocess)", "Maximum Elongation under load after cooling")]: "max_elongation_under_load_after_cooling",
  [aliasKey("Hot Set test (Inprocess)", "Minimum Elongation at break")]: "min_elongation_at_break",

  [aliasKey("Insulation Screening", "Filler (Non-Hygroscopic)")]: "filler_non_hygroscopic",
  // NOTE: only column left for a screening-related short-circuit rating is
  // "short_circuit_rating_1sec_conductor" — please confirm this is the right
  // one for Insulation Screening rather than a genuinely missing column.
  [aliasKey("Insulation Screening", "Short circuit rating in 1 sec")]: "short_circuit_rating_1sec_conductor",
  [aliasKey("Insulation Screening", "tolerance on thickness of covering minimum at any one point")]: "tolerance_on_thickness_of_covering",

  [aliasKey("Outer Sheathing", "Short Circuit withstand capacity of Conductor")]: "short_circuit_withstand_capacity_of_conductor",
  [aliasKey("Outer Sheathing", "Duration of short circuit")]: "duration_of_short_circuit",

  [aliasKey("Polyethylene Sheath Over Core", "Method of application")]: "method_of_application_of_insulation",

  [aliasKey("Thermal Stability Test for Insulation", "Elongation at break")]: "elongation_at_break",
  [aliasKey("Thermal Stability Test for Insulation", "Tensile Strength at break")]: "tensile_strength_at_break",
  [aliasKey("Armouring", "Mass of Zinc coating (for GI)")]: "mass_of_zinc_coating",
};

function normalizeKey(str) {
  const ABBR = {
    minimum: "min", maximum: "max", number: "no",
    diameter: "dia", percentage: "percent",
    temperature: "temp", thickness: "thk",
  };
  const words = String(str || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  return words.map((w) => ABBR[w] || w).join("");
}
function normalize(str) { return normalizeKey(str); }

/* Consistent, collision-safe process-name normalizer used ONLY for building
   the `CHAR__<process>__<characteristic>` keys in selectedCharacteristics.
   Bug fix: previously the raw process_name string was used directly in the
   key. That string comes from two different sources with different casing —
   the master Quality Configuration list (e.g. "OTHER DETAILS") vs. what was
   actually saved on a GTP previously (e.g. "Other Details"). Because the key
   is case-sensitive, the SAME characteristic under those two casings created
   TWO different keys in state. When saving, the backend's case-insensitive
   dedup then silently dropped one of them — which is why newly "select all"
   -checked characteristics could vanish and never show up in View mode.
   Trimming + lowercasing here guarantees both paths always produce the same
   key. */
function normProcessKey(processName) {
  return (processName || "").trim().toLowerCase();
}
function charKey(processName, characteristic) {
  return `CHAR__${normProcessKey(processName)}__${(characteristic || "").trim()}`;
}

/* Compound key: Process + Characteristic. Used because the same
   characteristic label (e.g. "Material") appears under multiple Processes
   but points at different columns in Routing / TBA-GTP / QA-FG. */
function aliasKey(processName, characteristic) {
  return `${normalizeKey(processName)}||${normalizeKey(characteristic)}`;
}

function getProductMasterValue(productDetailValues, characteristic, aliasMap = null, processName = null) {
  const normChar = normalize(characteristic);
  if (!normChar) return "";
  // 1) Exact alias mapping takes priority — try the process-qualified key
  //    first (disambiguates repeated labels like "Material"), then fall
  //    back to a characteristic-only key for alias maps that don't need it.
  if (aliasMap) {
    const compoundKey = processName ? aliasKey(processName, characteristic) : null;
    const aliasCol = (compoundKey && aliasMap[compoundKey]) || aliasMap[normChar];
    if (aliasCol && productDetailValues[aliasCol] != null) {
      const strVal = String(productDetailValues[aliasCol]);
      if (strVal !== "null") return strVal;
    }
  }
  // 2) Fall back to fuzzy substring matching against column names
  let bestKey = null, bestScore = 0;
  for (const col of Object.keys(productDetailValues)) {
    const normCol = normalize(col);
    if (!normCol) continue;
    let score = 0;
    if (normCol === normChar) score = normCol.length + 1000;
    else if (normChar.includes(normCol) || normCol.includes(normChar))
      score = Math.min(normCol.length, normChar.length);
    if (score > bestScore) { bestScore = score; bestKey = col; }
  }
  if (bestKey && productDetailValues[bestKey] != null) {
    const strVal = String(productDetailValues[bestKey]);
    return strVal === "null" ? "" : strVal;
  }
  return "";
}

const toOpts = (arr) => (arr || []).map((v) => ({ value: String(v), label: String(v) }));
const optsToParam = (opts) => (opts || []).map((o) => o.value).join(",");

function GTPConfiguration() {
  /* ================= STATE ================= */
  const [qualityConfigurations, setQualityConfigurations] = useState([]);
  const [selectedCharacteristics, setSelectedCharacteristics] = useState({});
  const [gtpList, setGtpList] = useState([]);
  const [productFormatList, setProductFormatList] = useState([]);
  const [pfCurrentPage, setPfCurrentPage] = useState(1);
  const [pfPageSize, setPfPageSize] = useState(10);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedGtpId, setSelectedGtpId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState("particulars");
  const [isStdValueEditMode, setIsStdValueEditMode] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showDatasheetPreview, setShowDatasheetPreview] = useState(false);
  const [previewDocLabel, setPreviewDocLabel] = useState("Datasheet");
  const [previewCharacteristics, setPreviewCharacteristics] = useState([]);
  const [previewTitlePrefix, setPreviewTitlePrefix] = useState("GUARANTEED TECHNICAL PARTICULARS FOR");
  const [tableView, setTableView] = useState("gtp");
  const [viewContext, setViewContext] = useState("gtp");
  const [checkedBy, setCheckedBy] = useState("");
  const [productDetailValues, setProductDetailValues] = useState({});
  const [bomValues, setBomValues] = useState({});
  const [tbaGtpValues, setTbaGtpValues] = useState({});
  const [routingValues, setRoutingValues] = useState({});
  const [qafgValues, setQafgValues] = useState({});
  const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office) — used for GTP "Name & Address of the manufacturer"

// Change this block inside getCurrentUser:
const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);

const getCurrentUser = () => {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
      if (fullName) return capitalizeFirst(fullName);
      if (parsedUser.name) return capitalizeFirst(parsedUser.name);
      if (parsedUser.username) return capitalizeFirst(parsedUser.username);
      if (parsedUser.email) return capitalizeFirst(parsedUser.email.split("@")[0]);
      if (parsedUser.role) return parsedUser.role.charAt(0).toUpperCase() + parsedUser.role.slice(1);
    } catch (e) { console.error("Error parsing user data:", e); }
  }
  const email = localStorage.getItem("email");
  if (email && email !== "undefined" && email !== "null") return capitalizeFirst(email);
  const storedName = localStorage.getItem("userName");
  if (storedName && storedName !== "undefined" && storedName !== "null") return capitalizeFirst(storedName);
  return "";
};

useEffect(() => {
  setCheckedBy(getCurrentUser());
}, []);

useEffect(() => {
  const loadUnit1Location = async () => {
    try {
      const res = await fetch(`${MASTER_API}/location-master`);
      const data = await res.json();
      const locations = Array.isArray(data) ? data : [];
      const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
      setUnit1Location(unit1);
    } catch (err) {
      console.error("LOCATION MASTER FETCH ERROR", err);
      setUnit1Location(null);
    }
  };
  loadUnit1Location();
}, []);

// "Name & Address of the manufacturer" for the GTP datasheet — always Unit 1
// (Regd. Office), sourced from Configuration > Location Master. Falls back to
// the static text only if Location Master hasn't been set up yet.
const manufacturerName = unit1Location?.company_name || "SBEE Cables (India) Limited";
const manufacturerAddress = unit1Location
  ? `${manufacturerName}, ${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
  : "SBEE Cables (India) Limited, Plot No. -282 & 283, 2nd Phase KIADB Industrial Area, Harohalli Village, Kanakapura Taluk, Ramanagar District";

  /* ── Assign GTP Modal (Product Format tab) ── */
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignGtpSelected, setAssignGtpSelected] = useState(null);
  const [assignMatchCount, setAssignMatchCount] = useState(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignGtpCharacteristics, setAssignGtpCharacteristics] = useState([]); // characteristics for selected GTP
  const [assignStdValues, setAssignStdValues] = useState({}); // { characteristic: stdValue }

  /* ── Assignment tab state ── */
  const [assignmentList, setAssignmentList]             = useState([]);
  const [showEditAssignModal, setShowEditAssignModal]   = useState(false);
  const [editAssignRow, setEditAssignRow]               = useState(null);
  const [editAssignChars, setEditAssignChars]           = useState([]);
  const [editAssignStdValues, setEditAssignStdValues]   = useState({});
  const [editAssignGtpType, setEditAssignGtpType]       = useState({}); // key -> Set of "Sales"/"Master"
  const [editAssignMasterDoc, setEditAssignMasterDoc]   = useState({}); // key -> boolean
  const [editAssignSaving, setEditAssignSaving]         = useState(false);
  const [editAssignMode, setEditAssignMode]             = useState("view"); // "view" | "edit"

  const [masterData, setMasterData] = useState({
    productTypes: [], products: [], subCategories: [], qaCategories: [],
  });

  /* ── GTP tab filter state ── */
  const [gtpFilterSelections, setGtpFilterSelections] = useState({
    category: [], cableType: [], code: [], colour: [],
    noOfCores: [], nominalCrossSection: [], referenceOfStandard: [],
    voltage: [], construction: [],
  });
  const [gtpCascadingOptions, setGtpCascadingOptions] = useState({
    categories: [], cableTypes: [], codes: [], colours: [], cores: [],
    nominalCrossSections: [], standards: [], voltages: [], constructions: [],
  });
  const [gtpMatchCount, setGtpMatchCount] = useState(null);
  const PRODUCT_TYPE = "Finished Goods";

  /* ── Product Format tab filter state ── */
  const [prodFilterSelections, setProdFilterSelections] = useState({
    category: [], cableType: [], code: [], colour: [],
    noOfCores: [], nominalCrossSection: [], referenceOfStandard: [],
    voltage: [], construction: [],
  });
  const [prodCascadingOptions, setProdCascadingOptions] = useState({
    categories: [], cableTypes: [], codes: [], colours: [], cores: [],
    nominalCrossSections: [], standards: [], voltages: [], constructions: [],
  });
  const [prodMatchCount, setProdMatchCount] = useState(null);

  /* ── Product Format tab filter state ── */
  const [pfFilterSelections, setPfFilterSelections] = useState({
    category: [], cableType: [], code: [], colour: [],
    noOfCores: [], nominalCrossSection: [], referenceOfStandard: [],
    voltage: [], construction: [],
  });
  const [pfCascadingOptions, setPfCascadingOptions] = useState({
    categories: [], cableTypes: [], codes: [], colours: [], cores: [],
    nominalCrossSections: [], standards: [], voltages: [], constructions: [],
  });
  const [pfMatchCount, setPfMatchCount] = useState(null);
  const [pfProductCodeFilter, setPfProductCodeFilter] = useState("");
  const [pfProductDescFilter, setPfProductDescFilter] = useState("");

  useEffect(() => {
    setPfCurrentPage(1);
  }, [pfFilterSelections, pfProductCodeFilter, pfProductDescFilter, productFormatList]);

  const [formData, setFormData] = useState({
    gtpNumber: "", productType: "", productCode: "", productDescription: "",
    cableType: "", construction: "", code: "", noOfCores: "",
    nominalCrossSection: "", colour: "", voltage: "", category: "",
    subCategory: "", qaCategory: "", productGroup: "",
    referenceOfStandard: "", isiMarked: "",
  });

  /* ================= EFFECTS ================= */
  useEffect(() => {
    fetchProductTypes();
    fetchProductList();
    fetchGtpList();
    fetchProductFormatList();
    fetchAssignmentList();
  }, []);

  useEffect(() => {
    fetchQualityConfigurations(PRODUCT_TYPE);
  }, []);

  useEffect(() => {
    if (!formData.productCode) {
      setProductDetailValues({}); setBomValues({});
      setTbaGtpValues({}); setRoutingValues({}); setQafgValues({});
      return;
    }
    fetchProductDetailValues(formData.productCode);
    fetchBomValues(formData.productCode);
    fetchTbaGtpValues(formData.productCode);
    fetchRoutingValues(formData.productCode);
    fetchQafgValues(formData.productCode);
  }, [formData.productCode]);

  /* Cascading options for GTP tab */
  useEffect(() => {
    const params = buildFilterParams(gtpFilterSelections, PRODUCT_TYPE);
    fetchGtpCascadingOptions(params);
    fetchGtpMatchCount(params);
  }, [gtpFilterSelections]);

  /* Cascading options for Product Format tab */
  useEffect(() => {
    const params = buildFilterParams(prodFilterSelections, PRODUCT_TYPE);
    fetchProdCascadingOptions(params);
    fetchProdMatchCount(params);
  }, [prodFilterSelections]);

  /* Cascading options for Product Format (pf) tab */
  useEffect(() => {
    const params = buildFilterParams(pfFilterSelections, PRODUCT_TYPE);
    fetchPfCascadingOptions(params);
    fetchPfMatchCount(params);
  }, [pfFilterSelections]);

  /* Backfill standardValue */
  useEffect(() => {
    if (
      Object.keys(productDetailValues).length === 0 &&
      Object.keys(bomValues).length === 0 &&
      Object.keys(tbaGtpValues).length === 0 &&
      Object.keys(routingValues).length === 0 &&
      Object.keys(qafgValues).length === 0
    ) return;
    setSelectedCharacteristics((prev) => {
      let changed = false;
      const updated = { ...prev };
      Object.entries(updated).forEach(([key, val]) => {
        if (key.startsWith("GROUP__")) return;
        if (val.standardValue && val.standardValue !== "null") return;
        let autoValue = "";
        if (val.source === "Product Master") autoValue = getProductMasterValue(productDetailValues, val.characteristic);
        else if (val.source === "QA-FG") autoValue = getProductMasterValue(qafgValues, val.characteristic, QAFG_ALIASES, val.processName);
        else if (val.source === "Routing") autoValue = getProductMasterValue(routingValues, val.characteristic, ROUTING_ALIASES, val.processName);
        else if (val.source === "TBA-GTP") autoValue = getProductMasterValue(tbaGtpValues, val.characteristic, TBA_GTP_ALIASES, val.processName);
        if (autoValue) { updated[key] = { ...val, standardValue: autoValue }; changed = true; }
      });
      return changed ? updated : prev;
    });
  }, [productDetailValues, bomValues, tbaGtpValues, routingValues, qafgValues]);

  /* ================= HELPERS ================= */
  const buildFilterParams = (sel, productType) => {
    const p = {};
    if (productType) p.productType = productType;
    for (const [key, opts] of Object.entries(sel)) {
      const joined = optsToParam(opts);
      if (joined) p[key] = joined;
    }
    return p;
  };

  /* ================= API CALLS ================= */
  const fetchProductTypes = async () => {
    try {
      const [ptRes, subCatRes, qaCatRes] = await Promise.all([
        axios.get(`${MASTER_API}/configuration/product-types`),
        axios.get(`${MASTER_API}/configuration/sub-category`),
        axios.get(`${MASTER_API}/configuration/quality-category`),
      ]);
      setMasterData((prev) => ({
        ...prev,
        productTypes:  ptRes.data  || [],
        subCategories: subCatRes.data || [],
        qaCategories:  qaCatRes.data  || [],
      }));
    } catch (err) { console.error("fetchProductTypes error:", err); }
  };

  const fetchProductList = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/product-master/products`, { params: { limit: 1000 } });
      setMasterData((prev) => ({ ...prev, products: res.data?.data || res.data || [] }));
    } catch (err) { console.error("fetchProductList error:", err); }
  };

  const fetchGtpList = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/all`);
      setGtpList(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchProductFormatList = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/all-product-format`);
      setProductFormatList(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchAssignmentList = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/assignment-list`);
      setAssignmentList(res.data || []);
    } catch (err) { console.error(err); }
  };

  const groupQualityRows = (rows) => {
    const groupMap = new Map();
    for (const row of rows) {
      const key = row.process_name || "";
      if (!groupMap.has(key)) {
        groupMap.set(key, { process_id: row.process_id, process_name: row.process_name, sub_process_name: "", characteristics: [] });
      }
      const group = groupMap.get(key);
      if (!group.characteristics.some((c) => c.characteristic === row.characteristic)) {
        group.characteristics.push({
          process_id: row.process_id, process_name: row.process_name,
          sub_process_id: row.sub_process_id, sub_process_name: row.sub_process_name || "",
          characteristic: row.characteristic, uom: row.uom || "",
        });
      }
    }
    return Array.from(groupMap.values());
  };

  const fetchQualityConfigurations = async (productType) => {
    try {
      const res = await axios.get(`${MASTER_API}/quality-configuration/module-characteristics`, { params: { productType, module: "GTP" } });
      setQualityConfigurations(groupQualityRows(res.data || []));
    } catch (err) { console.error(err); }
  };

  /* Cascading options — GTP tab */
  const fetchGtpCascadingOptions = useCallback(async (params) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/cascading-filters`, { params });
      const d = res.data || {};
      setGtpCascadingOptions({
        categories: d.categories || [], cableTypes: d.cableTypes || [],
        codes: d.codes || [], colours: d.colours || [], cores: d.cores || [],
        nominalCrossSections: d.nominalCrossSections || [], standards: d.standards || [],
        voltages: d.voltages || [], constructions: d.constructions || [],
      });
    } catch (err) { console.error("fetchGtpCascadingOptions error:", err); }
  }, []);

  const fetchGtpMatchCount = useCallback(async (params) => {
    const hasAny = Object.values(params).some(Boolean);
    if (!hasAny) { setGtpMatchCount(null); return; }
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/count-matching`, { params });
      setGtpMatchCount(res.data?.count ?? null);
    } catch (err) { setGtpMatchCount(null); }
  }, []);

  /* Cascading options — Product Format tab */
  const fetchProdCascadingOptions = useCallback(async (params) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/cascading-filters`, { params });
      const d = res.data || {};
      setProdCascadingOptions({
        categories: d.categories || [], cableTypes: d.cableTypes || [],
        codes: d.codes || [], colours: d.colours || [], cores: d.cores || [],
        nominalCrossSections: d.nominalCrossSections || [], standards: d.standards || [],
        voltages: d.voltages || [], constructions: d.constructions || [],
      });
    } catch (err) { console.error("fetchProdCascadingOptions error:", err); }
  }, []);

  const fetchProdMatchCount = useCallback(async (params) => {
    const hasAny = Object.values(params).some(Boolean);
    if (!hasAny) { setProdMatchCount(null); return; }
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/count-matching`, { params });
      setProdMatchCount(res.data?.count ?? null);
    } catch (err) { setProdMatchCount(null); }
  }, []);

  /* Cascading options — Product Format (pf) tab */
  const fetchPfCascadingOptions = useCallback(async (params) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/cascading-filters`, { params });
      const d = res.data || {};
      setPfCascadingOptions({
        categories: d.categories || [], cableTypes: d.cableTypes || [],
        codes: d.codes || [], colours: d.colours || [], cores: d.cores || [],
        nominalCrossSections: d.nominalCrossSections || [], standards: d.standards || [],
        voltages: d.voltages || [], constructions: d.constructions || [],
      });
    } catch (err) { console.error("fetchPfCascadingOptions error:", err); }
  }, []);

  const fetchPfMatchCount = useCallback(async (params) => {
    const hasAny = Object.values(params).some(Boolean);
    if (!hasAny) { setPfMatchCount(null); return; }
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/count-matching`, { params });
      setPfMatchCount(res.data?.count ?? null);
    } catch (err) { setPfMatchCount(null); }
  }, []);

  const fetchProductDetailValues = async (productCode) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/product-detail-values`, { params: { productCode } });
      setProductDetailValues(res.data || {});
    } catch (err) { console.error(err); setProductDetailValues({}); }
  };

  const fetchBomValues = async (productCode) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/bom-values`, { params: { productCode } });
      setBomValues(res.data || {});
    } catch (err) { console.error(err); setBomValues({}); }
  };

  const fetchTbaGtpValues = async (productCode) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/tba-gtp-values`, { params: { productCode } });
      setTbaGtpValues(res.data || {});
    } catch (err) { console.error(err); setTbaGtpValues({}); }
  };

  const fetchRoutingValues = async (productCode) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/routing-values`, { params: { productCode } });
      setRoutingValues(res.data || {});
    } catch (err) { console.error(err); setRoutingValues({}); }
  };

  const fetchQafgValues = async (productCode) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/qafg-values`, { params: { productCode } });
      setQafgValues(res.data || {});
    } catch (err) { console.error(err); setQafgValues({}); }
  };

  /* ================= ASSIGN GTP (Product Format tab) ================= */
  const handleOpenAssignModal = () => {
    const activeFilterCount = Object.values(prodFilterSelections).reduce((s, a) => s + (a?.length || 0), 0);
    if (activeFilterCount === 0) { alert("Please select at least one filter to assign a GTP"); return; }
    setAssignGtpSelected(null);
    setAssignGtpCharacteristics([]);
    setAssignStdValues({});
    setAssignMatchCount(prodMatchCount);
    setShowAssignModal(true);
  };

  const handleAssignGtpSelect = async (selected) => {
    setAssignGtpSelected(selected);
    setAssignGtpCharacteristics([]);
    setAssignStdValues({});
    if (!selected) return;
    try {
      // Find product_type for the selected GTP from gtpList
      const selectedGtp = gtpList.find(g => g.gtp_number === selected.value);
      const productType = selectedGtp?.product_type || PRODUCT_TYPE;

      // Fetch GTP details AND master quality config in parallel
      const [res, configRes] = await Promise.all([
        axios.get(`${MASTER_API}/gtp-configuration/detail/${selected.value}`),
        axios.get(`${MASTER_API}/quality-configuration/module-characteristics`, {
          params: { productType, module: "GTP" }
        }),
      ]);

      const data = res.data || [];

      // Build a set of valid processName||characteristic from the master list
      // so stale/extra parameters saved in DB don't appear in this modal
      const masterRows = configRes.data || [];
      const masterSet = new Set(
        masterRows.map(r =>
          `${(r.process_name || "").toLowerCase()}||${(r.characteristic || "").trim().toLowerCase()}`
        )
      );

      const allChars = data.filter(r => r.characteristic).map(r => ({
        characteristic: r.characteristic?.trim(),
        processName: r.process_name || "",
        uom: r.uom || "",
        valueType: r.value_type || "",
      }));

      // Only keep characteristics that exist in the current master config
      const filtered = allChars.filter(c =>
  masterSet.has(
    `${(c.processName || "").toLowerCase()}||${(c.characteristic || "").trim().toLowerCase()}`
  )
);
const chars = filtered.length > 0 ? filtered : allChars;

      setAssignGtpCharacteristics(chars);
      // init std values empty
      const initVals = {};
      chars.forEach(c => { initVals[`${c.processName}||${c.characteristic}`] = ""; });
      setAssignStdValues(initVals);
    } catch (err) {
      console.error("Failed to fetch GTP characteristics", err);
    }
  };

  const handleAssignGtp = async () => {
    if (!assignGtpSelected) { alert("Please select a GTP Number"); return; }
    setAssignSaving(true);
    try {
      const allVals = (opts) => (opts || []).map((o) => o.value);
      const stdValues = assignGtpCharacteristics.map(c => ({
        characteristic: c.characteristic,
        processName: c.processName || "",
        standardValue: assignStdValues[`${c.processName}||${c.characteristic}`] || "",
      }));
      const payload = {
        productType: PRODUCT_TYPE,
        filterMulti: {
          category:            allVals(prodFilterSelections.category),
          cableType:           allVals(prodFilterSelections.cableType),
          code:                allVals(prodFilterSelections.code),
          colour:              allVals(prodFilterSelections.colour),
          noOfCores:           allVals(prodFilterSelections.noOfCores),
          nominalCrossSection: allVals(prodFilterSelections.nominalCrossSection),
          referenceOfStandard: allVals(prodFilterSelections.referenceOfStandard),
          voltage:             allVals(prodFilterSelections.voltage),
          construction:        allVals(prodFilterSelections.construction),
        },
        gtpNumber: assignGtpSelected.value,
        stdValues,
        checkedBy,
      };
      await axios.post(`${MASTER_API}/gtp-configuration/assign-gtp`, payload);
      toast.success("GTP assigned successfully!");
      setShowAssignModal(false);
      setAssignGtpSelected(null);
      setAssignGtpCharacteristics([]);
      setAssignStdValues({});
      fetchProductFormatList();
      fetchAssignmentList();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to assign GTP");
    } finally {
      setAssignSaving(false);
    }
  };

  /* ================= OPEN EDIT/VIEW ASSIGNMENT MODAL ================= */
  const handleOpenEditAssign = async (row, mode = "view") => {
    setEditAssignRow(row);
    setEditAssignMode(mode);
    setEditAssignChars([]);
    setEditAssignStdValues({});
    setShowEditAssignModal(true);
    try {
      // Fetch one linked product code so we load its saved std values via COALESCE
      let firstProductCode = row.first_product_code || null;
      if (!firstProductCode) {
        try {
          const pfRes = await axios.get(`${MASTER_API}/gtp-configuration/all-product-format`);
          const linked = (pfRes.data || []).filter(p => p.gtp_number === row.gtp_number);
          firstProductCode = linked[0]?.productCode || null;
        } catch (e) { /* ignore */ }
      }

      // Fetch GTP details AND master quality config in parallel
      const [res, configRes] = await Promise.all([
        firstProductCode
          ? axios.get(`${MASTER_API}/gtp-configuration/detail/${row.gtp_number}`, { params: { productCode: firstProductCode } })
          : axios.get(`${MASTER_API}/gtp-configuration/detail/${row.gtp_number}`),
        axios.get(`${MASTER_API}/quality-configuration/module-characteristics`, {
          params: { productType: row.product_type || "Finished Goods", module: "GTP" }
        }),
      ]);

      const data = res.data || [];

      // Build a set of valid processName||characteristic from the master list.
      // This ensures stale/extra parameters saved in DB are not shown in the modal.
      const masterRows = configRes.data || [];
      const masterSet = new Set(
        masterRows.map(r =>
          `${(r.process_name || "").toLowerCase()}||${(r.characteristic || "").trim().toLowerCase()}`
        )
      );

      const allChars = data.filter(r => r.characteristic).map(r => ({
        characteristic: r.characteristic?.trim(),
        processName:    r.process_name    || "",
        uom:            r.uom             || "",
        standardValue:  r.standard_value  || "",
        gtpType:        r.gtp_type        || "",
        masterDoc:      !!r.master_doc,
      }));

      // Only keep characteristics that exist in the current master config
     const filtered = allChars.filter(c =>
  masterSet.has(
    `${(c.processName || "").toLowerCase()}||${(c.characteristic || "").trim().toLowerCase()}`
  )
);
const chars = filtered.length > 0 ? filtered : allChars;

      setEditAssignChars(chars);
      const initVals = {};
      const initGtpType = {};
      const initMasterDoc = {};
      chars.forEach(c => {
        const key = `${c.processName}||${c.characteristic}`;
        initVals[key] = c.standardValue;
        initGtpType[key] = new Set((c.gtpType || "").split(",").map(t => t.trim()).filter(Boolean));
        initMasterDoc[key] = !!c.masterDoc;
      });
      setEditAssignStdValues(initVals);
      setEditAssignGtpType(initGtpType);
      setEditAssignMasterDoc(initMasterDoc);
    } catch (err) {
      console.error("Failed to fetch GTP characteristics", err);
    }
  };

  /* ================= SAVE EDIT ASSIGNMENT (bulk propagate) ================= */
  const handleSaveEditAssign = async () => {
    if (!editAssignRow) return;
    setEditAssignSaving(true);
    try {
   const stdValues = editAssignChars.map(c => {
  const key = `${c.processName}||${c.characteristic}`;
  return {
    characteristic: c.characteristic,
    processName:    c.processName || "",
    standardValue:  editAssignStdValues[key] ?? "",
    gtpType:        Array.from(editAssignGtpType[key] || []).join(","),
    masterDoc:      !!editAssignMasterDoc[key],
  };
});
const payload = { 
  gtpNumber: editAssignRow.gtp_number, 
  filterMulti: {}, 
  stdValues, 
  checkedBy 
};
      const res = await axios.put(
        `${MASTER_API}/gtp-configuration/update-assignment-std-values`,
        payload
      );
      toast.success(`Standard values updated for ${res.data?.updatedCount ?? "all"} product(s)`);
      setShowEditAssignModal(false);
      setEditAssignRow(null);
      setEditAssignChars([]);
      setEditAssignStdValues({});
      setEditAssignMode("view");
      fetchAssignmentList();
      fetchProductFormatList();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Save failed");
    } finally {
      setEditAssignSaving(false);
    }
  };

  /* ================= DELETE GTP TEMPLATE ================= */
  const handleDeleteGtp = async (gtpNumber) => {
    if (!window.confirm(`Delete GTP "${gtpNumber}"? This will also clear it from all linked products.`)) return;
    try {
      await axios.delete(`${MASTER_API}/gtp-configuration/delete-gtp/${gtpNumber}`);
      toast.success("GTP deleted");
      fetchGtpList();
      fetchProductFormatList();
      fetchAssignmentList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  /* ================= DELETE ASSIGNMENT ================= */
  const handleDeleteAssignment = async (gtpNumber) => {
    if (!window.confirm(`Remove all product assignments for GTP "${gtpNumber}"?`)) return;
    try {
      await axios.delete(`${MASTER_API}/gtp-configuration/delete-assignment/${gtpNumber}`);
      toast.success("Assignment removed");
      fetchAssignmentList();
      fetchProductFormatList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  /* ================= DELETE PRODUCT FORMAT ROW ================= */
  const handleDeleteProductFormat = async (gtpNumber, productCode) => {
    if (!window.confirm(`Unlink product "${productCode}" from GTP "${gtpNumber}"?`)) return;
    try {
      await axios.delete(`${MASTER_API}/gtp-configuration/delete-product-format`, {
        params: { gtpNumber, productCode },
      });
      toast.success("Product unlinked");
      fetchProductFormatList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  /* ================= FORM HELPERS ================= */
  const getOptions = (list) => {
    if (!list) return [];
    return list.map((item) => {
      const value = item.productCode || item.name || item.group_name || item.code || item.core || item.colour || item.category_name || item;
      return { value, label: value };
    });
  };

  const resetForm = () => {
    setFormData({
      gtpNumber: "", productType: "", cableType: "", construction: "", code: "", productCode: "",
      productDescription: "", noOfCores: "", nominalCrossSection: "", colour: "", voltage: "",
      category: "", subCategory: "", qaCategory: "", productGroup: "", referenceOfStandard: "", isiMarked: "",
    });
    setSelectedCharacteristics({});
    setProductDetailValues({});
    setBomValues({});
    setViewContext("gtp");
    setIsViewMode(false);
    setIsEditMode(false);
    setSelectedGtpId(null);
    setIsStdValueEditMode(false);
    const userData = JSON.parse(localStorage.getItem("user"));
setCheckedBy(getCurrentUser());  };

  const populateGtpData = (data) => {
    if (!data || data.length === 0) return;
    const firstRow = data[0];
    setFormData({
      gtpNumber:           firstRow.gtp_number            || "",
      productType:         firstRow.product_type          || "",
      cableType:           firstRow.cable_type            || "",
      construction:        firstRow.construction          || "",
      code:                firstRow.code                  || "",
      productCode:         firstRow.productCode || firstRow.product_code || "",
      productDescription:  firstRow.product_description   || "",
      noOfCores:           firstRow.no_of_cores           || "",
      nominalCrossSection: firstRow.nominal_cross_section || "",
      colour:              firstRow.colour                || "",
      voltage:             firstRow.voltage               || "",
      category:            firstRow.category              || "",
      subCategory:         firstRow.sub_category          || "",
      qaCategory:          firstRow.qa_category           || "",
      productGroup:        firstRow.product_group         || "",
      referenceOfStandard: firstRow.reference_of_standard || "",
      isiMarked:           firstRow.isi_marked            || "",
    });
    const selected = {};
    let loadedCheckedBy = "";
    data.forEach((row) => {
      if (!row.characteristic) return;
      const key = charKey(row.process_name, row.characteristic);
      const gtpTypeArr = (row.gtp_type || "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => ({ value: v, label: v }));
      selected[key] = {
        processId: row.process_id, processName: row.process_name,
        subProcessId: row.sub_process_id, subProcessName: row.sub_process_name,
        characteristic: row.characteristic?.trim(), uom: row.uom,
        source: row.source || "", type: row.value_type || "", standardValue: row.standard_value || "",
        gtpType: gtpTypeArr,
        masterDoc: !!Number(row.master_doc),
        order: row.display_order ?? row.order ?? "",
      };
      selected[`GROUP__${row.process_name}`] = { order: row.display_order ?? row.order ?? "" };
      if (!loadedCheckedBy && row.checked_by) loadedCheckedBy = row.checked_by;
    });
    setSelectedCharacteristics(selected);
    const userData = JSON.parse(localStorage.getItem("user"));
setCheckedBy(loadedCheckedBy || getCurrentUser());  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      const gtpNo = formData.gtpNumber?.trim();
      if (!gtpNo) { alert("GTP Number is mandatory"); return; }
      const charEntries = Object.entries(selectedCharacteristics).filter(([key]) => !key.startsWith("GROUP__"));
      if (charEntries.length === 0) { alert("Please select at least one characteristic"); return; }
      const mappedCharacteristics = {};
      charEntries.forEach(([key, val]) => {
        const groupOrder = selectedCharacteristics[`GROUP__${val.processName}`]?.order || "";
        mappedCharacteristics[key] = { ...val, testType: val.source, gtpType: (val.gtpType || []).map((o) => o.value).join(","), order: groupOrder };
      });
      const firstVal = (opts) => opts?.[0]?.value || "";
      const allVals  = (opts) => (opts || []).map((o) => o.value);
      const sel = gtpFilterSelections;
      const payload = {
        ...formData,
        productType: PRODUCT_TYPE,
        category: firstVal(sel.category), cableType: firstVal(sel.cableType),
        code: firstVal(sel.code), colour: firstVal(sel.colour),
        noOfCores: firstVal(sel.noOfCores), nominalCrossSection: firstVal(sel.nominalCrossSection),
        referenceOfStandard: firstVal(sel.referenceOfStandard), voltage: firstVal(sel.voltage),
        construction: firstVal(sel.construction),
        filterMulti: {
          category: allVals(sel.category), cableType: allVals(sel.cableType),
          code: allVals(sel.code), colour: allVals(sel.colour),
          noOfCores: allVals(sel.noOfCores), nominalCrossSection: allVals(sel.nominalCrossSection),
          referenceOfStandard: allVals(sel.referenceOfStandard), voltage: allVals(sel.voltage),
          construction: allVals(sel.construction),
        },
        inspectionType: "",
        selectedCharacteristics: mappedCharacteristics,
      };
      await axios.post(`${MASTER_API}/gtp-configuration/save`, payload);
      toast.success("Saved Successfully.");
      setShowModal(false);
      resetForm();
      fetchGtpList();
      fetchProductFormatList();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Save Failed");
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    try {
      const charEntries = Object.entries(selectedCharacteristics).filter(([key]) => !key.startsWith("GROUP__"));
      const mappedCharacteristics = {};
      charEntries.forEach(([key, val]) => {
        const groupOrder = selectedCharacteristics[`GROUP__${val.processName}`]?.order || "";
        mappedCharacteristics[key] = { ...val, testType: val.source, gtpType: (val.gtpType || []).map((o) => o.value).join(","), order: groupOrder };
      });
      const payload = { ...formData, inspectionType: "", selectedCharacteristics: mappedCharacteristics };
      await axios.put(`${MASTER_API}/gtp-configuration/update/${selectedGtpId}`, payload);
      toast.success("Updated Successfully");
      fetchGtpList();
      fetchProductFormatList();
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Update Failed");
    }
  };

  /* ================= SAVE STD VALUES ================= */
  const handleProductStdValueSave = async () => {
    try {
      const stdValues = Object.entries(selectedCharacteristics)
        .filter(([key]) => !key.startsWith("GROUP__"))
        .map(([, val]) => ({
          characteristic: val.characteristic,
          processName:    val.processName || "",
          standardValue:  val.standardValue || "",
          gtpType:        (val.gtpType || []).map((o) => o.value).join(","),
          masterDoc:      !!val.masterDoc,
        }));
      const payload = { productCode: formData.productCode, checkedBy, stdValues };
      await axios.put(`${MASTER_API}/gtp-configuration/update-std-values/${selectedGtpId}`, payload);
      toast.success("Standard Values Saved Successfully");
      fetchProductFormatList();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Save Failed");
    }
  };

  /* ================= VIEW ================= */
  const handleView = async (gtpNumber, context = "gtp", gtpId = null, productCode = null) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/detail/${gtpNumber}`, productCode ? { params: { productCode } } : {});
      const data = res.data;
      if (!data || data.length === 0) { alert("GTP Configuration not found"); return; }
      const productType = data[0].product_type;
      const configRes = await axios.get(`${MASTER_API}/quality-configuration/module-characteristics`, { params: { productType, module: "GTP" } });
      const grouped = groupQualityRows(configRes.data || []);
      setQualityConfigurations(grouped);
      populateGtpData(data);
      setIsViewMode(true);
      if (gtpId) setSelectedGtpId(gtpId);
      setIsEditMode(false);
      setViewContext(context);
      setShowModal(true);
      const pCode = productCode || data[0].product_code || data[0].productCode || "";
      if (pCode) {
        fetchProductDetailValues(pCode); fetchBomValues(pCode);
        fetchTbaGtpValues(pCode); fetchRoutingValues(pCode); fetchQafgValues(pCode);
      }
    } catch (err) { console.error(err); alert("Failed to load GTP data"); }
  };

  /* ================= EDIT ================= */
  const handleEdit = async (gtpId, context = "gtp", productCode = null) => {
    try {
      const res = await axios.get(`${MASTER_API}/gtp-configuration/edit/${gtpId}`);
      const data = res.data;
      if (!data || data.length === 0) { alert("GTP Configuration not found"); return; }
      const productType = data[0].product_type;
      const configRes = await axios.get(`${MASTER_API}/quality-configuration/module-characteristics`, { params: { productType, module: "GTP" } });
      setQualityConfigurations(groupQualityRows(configRes.data || []));
      if (context === "product" && productCode) {
        const gtpNumber = data[0].gtp_number;
        const detailRes = await axios.get(`${MASTER_API}/gtp-configuration/detail/${gtpNumber}`, { params: { productCode } });
        populateGtpData(detailRes.data, productCode);
      } else {
        populateGtpData(data);
      }
      setSelectedGtpId(gtpId);
      setIsEditMode(true);
      setIsViewMode(false);
      setViewContext(context);
      setShowModal(true);
      const pCode = productCode || data[0].product_code || data[0].productCode || "";
      if (pCode) {
        fetchProductDetailValues(pCode); fetchBomValues(pCode);
        fetchTbaGtpValues(pCode); fetchRoutingValues(pCode); fetchQafgValues(pCode);
      }
    } catch (err) { console.error(err); alert("Failed to load GTP data"); }
  };

  /* ================= CHARACTERISTIC SELECTION ================= */
  const handleCharacteristicSelect = (group, item) => {
    const key = charKey(item.process_name, item.characteristic);
    setSelectedCharacteristics((prev) => {
      const updated = { ...prev };
      if (updated[key]) delete updated[key];
      else updated[key] = {
        processId: item.process_id, processName: item.process_name,
        subProcessId: item.sub_process_id, subProcessName: item.sub_process_name,
        characteristic: item.characteristic, uom: item.uom || "",
        source: "", type: "", standardValue: "", gtpType: [], masterDoc: false,
      };
      return updated;
    });
  };

  const handleProductGroupSelect = (group, characteristics) => {
    const groupKey = `GROUP__${group}`;
    setSelectedCharacteristics((prev) => {
      const updated = { ...prev };
      if (updated[groupKey]) {
        delete updated[groupKey];
        characteristics.forEach((item) => delete updated[charKey(item.process_name, item.characteristic)]);
      } else updated[groupKey] = { order: "" };
      return updated;
    });
  };

  const handleGroupOrderChange = (group, value) => {
    const groupKey = `GROUP__${group}`;

    // If another (non-stale) process already holds this number, swap the two
    // orders instead of blocking the change — the other process takes whatever
    // order this one had before.
    if (value !== "") {
      const masterProcessSet = new Set(
        qualityConfigurations.map((g) => (g.process_name || "").trim().toLowerCase())
      );
      const holderKey = Object.keys(selectedCharacteristics).find((k) => {
        if (!k.startsWith("GROUP__") || k === groupKey) return false;
        const otherProcessName = k.slice("GROUP__".length).trim().toLowerCase();
        if (!masterProcessSet.has(otherProcessName)) return false; // stale, ignore
        return String(selectedCharacteristics[k]?.order) === String(value);
      });

      if (holderKey) {
        const previousOrder = selectedCharacteristics[groupKey]?.order ?? "";
        setSelectedCharacteristics((prev) => ({
          ...prev,
          [groupKey]: { ...(prev[groupKey] || {}), order: value },
          [holderKey]: { ...(prev[holderKey] || {}), order: previousOrder },
        }));
        const holderName = holderKey.slice("GROUP__".length);
        toast.success(`Swapped — "${holderName}" now has ${previousOrder || "no"} order`);
        return;
      }
    }

    setSelectedCharacteristics((prev) => ({
      ...prev,
      [groupKey]: { ...(prev[groupKey] || {}), order: value },
    }));
  };

  const handleSourceChange = (key, characteristic, newSource, processName = null) => {
    let autoValue = "";
    if (newSource === "Product Master") autoValue = getProductMasterValue(productDetailValues, characteristic);
    else if (newSource === "QA-FG") autoValue = getProductMasterValue(qafgValues, characteristic, QAFG_ALIASES, processName);
    else if (newSource === "Routing") autoValue = getProductMasterValue(routingValues, characteristic, ROUTING_ALIASES, processName);
    else if (newSource === "TBA-GTP") autoValue = getProductMasterValue(tbaGtpValues, characteristic, TBA_GTP_ALIASES, processName);
    setSelectedCharacteristics((prev) => ({ ...prev, [key]: { ...prev[key], source: newSource, standardValue: autoValue } }));
  };

  const handleGtpTypeChange = (key, opts) => {
    setSelectedCharacteristics((prev) => ({ ...prev, [key]: { ...prev[key], gtpType: opts || [] } }));
  };

  const handleMasterDocChange = (key, checked) => {
    setSelectedCharacteristics((prev) => ({ ...prev, [key]: { ...prev[key], masterDoc: checked } }));
  };

  /* ================= COMPUTED ================= */
  // Build a set of valid "processName||characteristic" pairs from the master list
  // so that only parameters that were explicitly saved AND still exist in the master
  // configuration are shown in view mode. This prevents stale/extra parameters
  // (saved by old code or accidental bulk-select) from appearing in the view.
  const masterCharSet = new Set(
    qualityConfigurations.flatMap((group) =>
      (group.characteristics || []).map(
        (c) => `${(c.process_name || "").toLowerCase()}||${(c.characteristic || "").trim().toLowerCase()}`
      )
    )
  );

  // Process groups that actually exist in the current master list. A GROUP__ entry
  // loaded from old saved data whose process no longer appears here is "stale" —
  // it shouldn't be allowed to lock an order number that's no longer really in use.
  const masterProcessSet = new Set(
    qualityConfigurations.map((group) => (group.process_name || "").trim().toLowerCase())
  );

  const viewCharacteristics = Object.entries(selectedCharacteristics)
    .filter(([key, val]) => {
      if (key.startsWith("GROUP__")) return false;
      // In view mode, only show characteristics that exist in the current master list
      // This removes any extra/stale parameters that may have been saved previously
      if (isViewMode && qualityConfigurations.length > 0) {
        const lookup = `${(val.processName || "").toLowerCase()}||${(val.characteristic || "").trim().toLowerCase()}`;
        return masterCharSet.has(lookup);
      }
      return true;
    })
    .sort(([, a], [, b]) => {
      const oA = a.order !== "" && a.order != null ? Number(a.order) : null;
      const oB = b.order !== "" && b.order != null ? Number(b.order) : null;
      if (oA !== null && oB !== null && oA !== oB) return oA - oB;
      if (oA !== null && oB === null) return -1;
      if (oA === null && oB !== null) return 1;
      const pA = (a.processName || "").toUpperCase(), pB = (b.processName || "").toUpperCase();
      if (pA < pB) return -1; if (pA > pB) return 1;
      const spA = (a.subProcessName || "").toUpperCase(), spB = (b.subProcessName || "").toUpperCase();
      return spA < spB ? -1 : spA > spB ? 1 : 0;
    });

  // Datasheet (PDF + preview) should only list characteristics that actually
  // have a specified/standard value for this product — rows with a blank
  // value are dropped instead of printing an empty cell.
  const datasheetCharacteristics = viewCharacteristics.filter(
    ([, val]) => val.standardValue !== undefined && val.standardValue !== null && String(val.standardValue).trim() !== ""
  );

  // Master Docs — same rule, but restricted to characteristics with the
  // "Master Doc" checkbox ticked (separate flag from the GTP Type dropdown).
  const masterDatasheetCharacteristics = datasheetCharacteristics.filter(
    ([, val]) => !!val.masterDoc
  );

  // Sales Docs — restricted to characteristics whose GTP Type includes "Sales".
  const salesDatasheetCharacteristics = datasheetCharacteristics.filter(
    ([, val]) => (val.gtpType || []).some((o) => o.value === "Sales")
  );

  /* ================= PDF ================= */
  const buildGtpPdf = (characteristics = datasheetCharacteristics, titlePrefix = "GUARANTEED TECHNICAL PARTICULARS FOR") => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40, contentW = pageWidth - margin * 2, headerTopY = 28;
    const logoH = 40, logoW = 60;
    try { doc.addImage(sbeeLogo, "PNG", margin, headerTopY, logoW, logoH); } catch (e) {}
    doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("SBEE CABLES INDIA LTD", pageWidth / 2, headerTopY + 15, { align: "center" });
    doc.setFontSize(10);
    doc.text(`${titlePrefix} ${(formData.productType || "FINISHED GOODS").toUpperCase()}`, pageWidth / 2, headerTopY + 30, { align: "center" });
    const barTopY = headerTopY + logoH + 12, barH = 20;
    doc.setDrawColor(51, 51, 51); doc.setLineWidth(0.75);
    doc.rect(margin, barTopY, contentW, barH);
    doc.setFontSize(9);
    let tx = margin + 8;
    const ty = barTopY + barH / 2 + 3.5;
    doc.setFont("helvetica", "bold"); doc.text("Product Code:", tx, ty); tx += doc.getTextWidth("Product Code:");
    doc.setFont("helvetica", "normal");
    const pcVal = ` ${formData.productCode || "-"}`;
    doc.text(pcVal, tx, ty); tx += doc.getTextWidth(pcVal);
    doc.setFont("helvetica", "bold"); doc.text("   Description:", tx, ty); tx += doc.getTextWidth("   Description:");
    doc.setFont("helvetica", "normal"); doc.text(` ${formData.productDescription || "-"}`, tx, ty);
    const tableStartY = barTopY + barH;
    const col0W = 45, col2W = 65, col1W = 185, col3W = contentW - col0W - col1W - col2W;
    const tableBody = [];
    tableBody.push([{ content: "1", styles: { fontStyle: "bold", halign: "center" } }, { content: "GENERAL", colSpan: 3, styles: { fontStyle: "bold" } }]);
    tableBody.push(["1.1", "Name & Address of the manufacturer", "", manufacturerAddress]);
    tableBody.push(["1.2", "Trade mark / Brand Name", "", ""]);
    const trademarkBodyRowIndex = 2;
    let lastHeader = null, sectionCounter = 1, subCounter = 0;
    characteristics.forEach(([, val]) => {
      const hdr = val.processName || "";
      if (hdr !== lastHeader) {
        sectionCounter++; subCounter = 0; lastHeader = hdr;
        tableBody.push([{ content: String(sectionCounter), styles: { fontStyle: "bold", halign: "center" } }, { content: hdr.toUpperCase(), colSpan: 3, styles: { fontStyle: "bold" } }]);
      }
      subCounter++;
      tableBody.push([`${sectionCounter}.${subCounter}`, val.characteristic || "", val.uom || "", val.standardValue || ""]);
    });
    autoTable(doc, {
      startY: tableStartY,
      head: [[{ content: "S.No", styles: { halign: "center" } }, "Parameters", { content: "UOM", styles: { halign: "center" } }, { content: "Specified Values", styles: { halign: "center" } }]],
      body: tableBody,
      columnStyles: { 0: { cellWidth: col0W, halign: "center" }, 1: { cellWidth: col1W }, 2: { cellWidth: col2W, halign: "center" }, 3: { cellWidth: col3W, halign: "center" } },
      styles: { fontSize: 8, cellPadding: 3, lineColor: [51, 51, 51], lineWidth: 0.5 },
      headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold" },
      theme: "grid",
      didDrawCell: (hookData) => {
        if (hookData.section === "body" && hookData.row.index === trademarkBodyRowIndex && hookData.column.index === 3) {
          try {
            const cX = hookData.cell.x + 2, cY = hookData.cell.y + 2, cH = hookData.cell.height - 4, cW = cH * (logoW / logoH);
            doc.addImage(sbeeLogo, "PNG", cX, cY, cW, cH);
          } catch (e) {}
        }
      },
    });
    const finalY = doc.lastAutoTable.finalY || tableStartY, footerY = finalY + 50;
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
  [["Prepared By", capitalizeFirst(checkedBy)], ["Reviewed By", ""], ["Approved By", ""]].forEach(([lbl, val], i) => {
  const x = margin + i * (contentW / 3) + 10;
  doc.text(lbl, x, footerY);
  if (val) doc.text(val, x, footerY + 14);
  doc.setDrawColor(0); doc.line(x, footerY + 20, x + 80, footerY + 20);
});
    return doc;
  };

  const handleViewPdf = () => { const doc = buildGtpPdf(datasheetCharacteristics); setPreviewDocLabel("Datasheet"); setPreviewCharacteristics(datasheetCharacteristics); setPreviewTitlePrefix("GUARANTEED TECHNICAL PARTICULARS FOR"); setPdfPreviewUrl(doc.output("bloburl")); setShowDatasheetPreview(true); };
  const handleDownloadPdf = () => { buildGtpPdf(datasheetCharacteristics).save(`${formData.gtpNumber || "GTP"}_Datasheet.pdf`); };
  const handlePrintPdf = () => { const url = buildGtpPdf(datasheetCharacteristics).output("bloburl"); const win = window.open(url); if (win) win.print(); };

  /* ── Master Docs — same PDF builder, restricted to GTP Type = Master rows ── */
  const handleViewMasterPdf = () => { const doc = buildGtpPdf(masterDatasheetCharacteristics, "MASTER DOCUMENT FOR"); setPreviewDocLabel("MasterDocs"); setPreviewCharacteristics(masterDatasheetCharacteristics); setPreviewTitlePrefix("MASTER DOCUMENT FOR"); setPdfPreviewUrl(doc.output("bloburl")); setShowDatasheetPreview(true); };
  const handleDownloadMasterPdf = () => { buildGtpPdf(masterDatasheetCharacteristics, "MASTER DOCUMENT FOR").save(`${formData.gtpNumber || "GTP"}_MasterDocs.pdf`); };
  const handlePrintMasterPdf = () => { const url = buildGtpPdf(masterDatasheetCharacteristics, "MASTER DOCUMENT FOR").output("bloburl"); const win = window.open(url); if (win) win.print(); };

  /* ── Sales Docs — same PDF builder, restricted to GTP Type = Sales rows ── */
  const handleViewSalesPdf = () => { const doc = buildGtpPdf(salesDatasheetCharacteristics, "SALES DOCUMENT FOR"); setPreviewDocLabel("SalesDocs"); setPreviewCharacteristics(salesDatasheetCharacteristics); setPreviewTitlePrefix("SALES DOCUMENT FOR"); setPdfPreviewUrl(doc.output("bloburl")); setShowDatasheetPreview(true); };
  const handleDownloadSalesPdf = () => { buildGtpPdf(salesDatasheetCharacteristics, "SALES DOCUMENT FOR").save(`${formData.gtpNumber || "GTP"}_SalesDocs.pdf`); };
  const handlePrintSalesPdf = () => { const url = buildGtpPdf(salesDatasheetCharacteristics, "SALES DOCUMENT FOR").output("bloburl"); const win = window.open(url); if (win) win.print(); };

  /* ================= STYLES ================= */
  const customSelectStyles = {
    control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: "13px", borderColor: "#dee2e6", boxShadow: "none", "&:hover": { borderColor: "#adb5bd" } }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 8px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    option: (base, state) => ({ ...base, fontSize: "13px", backgroundColor: state.isSelected ? "#021843" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
  };

  const filterSelectStyles = {
    control: (base) => ({ ...base, minHeight: 30, height: "auto", fontSize: "12px", borderColor: "#dee2e6", boxShadow: "none", "&:hover": { borderColor: "#adb5bd" } }),
    valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
    indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", paddingTop: 2 }),
    dropdownIndicator: (base) => ({ ...base, padding: 2 }),
    clearIndicator: (base) => ({ ...base, padding: 2 }),
    placeholder: (base) => ({ ...base, fontSize: "12px", color: "#94a3b8", fontWeight: 400 }),
    option: (base, state) => ({ ...base, fontSize: "12px", fontWeight: 400, color: state.isSelected ? "#fff" : "#334155", backgroundColor: state.isSelected ? "#475569" : state.isFocused ? "#f1f5f9" : "#fff", padding: "6px 10px" }),
    multiValue: (base) => ({ ...base, fontSize: "11px", backgroundColor: "#e2e8f0", borderRadius: 4 }),
    multiValueLabel: (base) => ({ ...base, fontSize: "11px", color: "#334155", padding: "1px 4px" }),
    multiValueRemove: (base) => ({ ...base, color: "#64748b", ":hover": { backgroundColor: "#cbd5e1", color: "#1e293b" } }),
    menu: (base) => ({ ...base, fontSize: "12px", zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  };

  const filterFields = [
    { key: "category",            optsKey: "categories",           label: "Category"      },
    { key: "cableType",           optsKey: "cableTypes",           label: "Cable Type"    },
    { key: "code",                optsKey: "codes",                label: "Code"          },
    { key: "colour",              optsKey: "colours",              label: "Color"         },
    { key: "noOfCores",           optsKey: "cores",                label: "Cores"         },
    { key: "nominalCrossSection", optsKey: "nominalCrossSections", label: "Cross Section" },
    { key: "referenceOfStandard", optsKey: "standards",            label: "Std. Ref"      },
    { key: "voltage",             optsKey: "voltages",             label: "Voltage"       },
    { key: "construction",        optsKey: "constructions",        label: "Construction"  },
  ];

  /* ── Active filter counts ── */
  const gtpActiveFilterCount = Object.values(gtpFilterSelections).reduce((s, a) => s + (a?.length || 0), 0);
  const prodActiveFilterCount = Object.values(prodFilterSelections).reduce((s, a) => s + (a?.length || 0), 0);
  const pfActiveFilterCount = Object.values(pfFilterSelections).reduce((s, a) => s + (a?.length || 0), 0);

  /* ── Filter bar renderer (reused for both tabs) ── */
  const renderFilterBar = ({ filterSelections, setFilterSelections, cascadingOptions, matchCount, activeFilterCount, clearFilters, addLabel, onAdd }) => (
    <div style={styles.filterCard}>
      {/* Cascading filter bar */}
      <>
          <div style={styles.filterBar}>
            {filterFields.map(({ key, optsKey, label }) => (
              <div key={key} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 180, display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={styles.filterLabel}>{label}</span>
                <Select
                  isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                  options={toOpts(cascadingOptions[optsKey])}
                  value={filterSelections[key]}
                  onChange={(opts) => setFilterSelections((prev) => ({ ...prev, [key]: opts || [] }))}
                  placeholder={label} isClearable
                  menuPortalTarget={document.body} menuPosition="fixed"
                  styles={filterSelectStyles}
                />
              </div>
            ))}
            {activeFilterCount > 0 && (
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 3, flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: "transparent" }}>x</span>
                <button onClick={clearFilters} style={styles.clearBtn}>✕ Clear ({activeFilterCount})</button>
              </div>
            )}
          </div>
          {matchCount !== null && activeFilterCount > 0 && (
            <div style={styles.matchHint}>
              <span style={{ color: matchCount > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                {matchCount > 0 ? `${matchCount} product${matchCount !== 1 ? "s" : ""} match` : "No products match the current filters"}
              </span>
            </div>
          )}
        </>

      {/* Add / Assign button */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
        <button
          onClick={onAdd}
          style={{
            background: "#021843",
            color: "#fff", border: "none", borderRadius: "4px",
            padding: "5px 16px", fontSize: "13px", fontWeight: 500, height: "32px",
            cursor: "pointer",
          }}
        >
          {addLabel}
        </button>
      </div>
    </div>
  );

  /* ================= RENDER ================= */
  return (
    <Layout>
      <div style={styles.pageTitleBar}>
        <div>
          <div style={styles.pageTitle}>GTP Configuration</div>
          <div style={styles.pageSubtitle}>Manage GTP Configuration Parameters</div>
        </div>
      </div>

      {/* ═══════════ TABLE TOGGLE ═══════════ */}
      <div style={styles.tableContainer}>
        <div style={{ ...styles.toggleBar, justifyContent: "flex-end" }}>
          <button style={{ ...styles.toggleBtn, ...(tableView === "gtp" ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => setTableView("gtp")}>GTP TEMPLATE</button>
          <button style={{ ...styles.toggleBtn, ...(tableView === "product" ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => { setTableView("product"); fetchAssignmentList(); }}>ASSIGNMENT</button>
          <button style={{ ...styles.toggleBtn, ...(tableView === "assignment" ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => { setTableView("assignment"); fetchAssignmentList(); }}>PRODUCT FORMAT</button>
        </div>

        {/* ═══════════ GTP FORMAT TAB ═══════════ */}
        {tableView === "gtp" && (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
              <button
                onClick={() => {
                  setIsViewMode(false); setIsEditMode(false); setSelectedGtpId(null); setViewContext("gtp");
                  setFormData((prev) => ({ ...prev, productType: PRODUCT_TYPE }));
                  fetchQualityConfigurations(PRODUCT_TYPE);
                  setShowModal(true);
                }}
                style={{ background: "#021843", color: "#fff", border: "none", borderRadius: "4px", padding: "5px 16px", fontSize: "13px", fontWeight: 500, height: "32px", cursor: "pointer" }}
              >
                Create 
              </button>
            </div>

            <table className="table" style={styles.erpTable}>
              <thead>
                <tr>
                  <th style={styles.erpTh}>GTP Number</th>
<th
  style={{
    ...styles.erpTh,
    textAlign: "right",
    width: "180px",
  }}
>
  Action
</th>
                </tr>
              </thead>
              <tbody>
                {gtpList.length > 0 ? gtpList.map((item) => (
                  <tr key={item.gtp_id}>
                    <td style={styles.erpTd}>{item.gtp_number}</td>
                    <td
  style={{
    ...styles.erpTd,
    textAlign: "right",
    whiteSpace: "nowrap",
  }}
>
  <button
    className="btn btn-outline-dark btn-sm me-2"
    onClick={() => handleView(item.gtp_number)}
  >
    View
  </button>

  <button
    className="btn btn-dark btn-sm me-2"
    onClick={() => handleEdit(item.gtp_id)}
  >
    Edit
  </button>

  <button
    className="btn btn-sm"
    onClick={() => handleDeleteGtp(item.gtp_number)}
    title="Delete"
    style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
  </button>
</td>
                  </tr>
                )) : (
                  <tr><td colSpan="2" className="text-center text-muted p-3">No Records Found</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {/* ═══════════ ASSIGNMENT TAB (filter + GTP Number/Product Code/Description table) ═══════════ */}
        {tableView === "product" && (
          <>
            {renderFilterBar({
              filterSelections: prodFilterSelections,
              setFilterSelections: setProdFilterSelections,
              cascadingOptions: prodCascadingOptions,
              matchCount: prodMatchCount,
              activeFilterCount: prodActiveFilterCount,
              clearFilters: () => { setProdFilterSelections({ category: [], cableType: [], code: [], colour: [], noOfCores: [], nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [] }); setProdMatchCount(null); },
              addLabel: "Assign ",
              onAdd: handleOpenAssignModal,
            })}

            <table className="table" style={styles.erpTable}>
              <thead>
                <tr>
                  <th style={styles.erpTh}>GTP Number</th>
                  <th style={styles.erpTh}>Category</th>
                  <th style={styles.erpTh}>Cable Type</th>
                  <th style={styles.erpTh}>Code</th>
                  <th style={styles.erpTh}>Color</th>
                  <th style={styles.erpTh}>Cores</th>
                  <th style={styles.erpTh}>Cross Section</th>
                  <th style={styles.erpTh}>Std. Ref</th>
                  <th style={styles.erpTh}>Voltage</th>
                  <th style={styles.erpTh}>Construction</th>
                  <th style={styles.erpTh}>Products Linked</th>
                  <th style={{ ...styles.erpTh, textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {assignmentList.length > 0 ? assignmentList.map((row) => (
                  <tr key={row.gtp_id}>
                    <td style={styles.erpTd}>{row.gtp_number}</td>
                    <td style={styles.erpTd}>{row.category || "—"}</td>
                    <td style={styles.erpTd}>{row.cableType || "—"}</td>
                    <td style={styles.erpTd}>{row.code || "—"}</td>
                    <td style={styles.erpTd}>{row.colour || "—"}</td>
                    <td style={styles.erpTd}>{row.noOfCores || "—"}</td>
                    <td style={styles.erpTd}>{row.nominalCrossSection || "—"}</td>
                    <td style={styles.erpTd}>{row.referenceOfStandard || "—"}</td>
                    <td style={styles.erpTd}>{row.voltage || "—"}</td>
                    <td style={styles.erpTd}>{row.construction || "—"}</td>
                    <td style={styles.erpTd}>
                      <span style={{ background: "#e0f2fe", color: "#0369a1", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                        {row.product_count} product{row.product_count !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="btn btn-outline-dark btn-sm me-2" onClick={() => handleOpenEditAssign(row, "view")}>View</button>
                      <button style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleDeleteAssignment(row.gtp_number)} title="Delete"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="12" className="text-center text-muted p-3">No assignments found</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}

   {/* ═══════════ PRODUCT FORMAT TAB ═══════════ */}
{tableView === "assignment" && (
  <>
    {/* Filter card */}
    <div style={styles.filterCard}>
      {/* Row 1: cascading dropdowns */}
      <div style={styles.filterBar}>
        {filterFields.map(({ key, optsKey, label }) => (
          <div key={key} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 180, display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={styles.filterLabel}>{label}</span>
            <Select
              isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
              options={toOpts(pfCascadingOptions[optsKey])}
              value={pfFilterSelections[key]}
              onChange={(opts) => setPfFilterSelections((prev) => ({ ...prev, [key]: opts || [] }))}
              placeholder={label} isClearable
              menuPortalTarget={document.body} menuPosition="fixed"
              styles={filterSelectStyles}
            />
          </div>
        ))}
        {pfActiveFilterCount > 0 && (
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 3, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: "transparent" }}>x</span>
            <button
              onClick={() => { setPfFilterSelections({ category: [], cableType: [], code: [], colour: [], noOfCores: [], nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [] }); setPfMatchCount(null); setPfProductCodeFilter(""); setPfProductDescFilter(""); }}
              style={styles.clearBtn}
            >✕ Clear ({pfActiveFilterCount})</button>
          </div>
        )}
      </div>

      {pfMatchCount !== null && pfActiveFilterCount > 0 && (
        <div style={styles.matchHint}>
          <span style={{ color: pfMatchCount > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
            {pfMatchCount > 0 ? `${pfMatchCount} product${pfMatchCount !== 1 ? "s" : ""} match` : "No products match"}
          </span>
        </div>
      )}

      {/* Row 2: Product Code + Description with auto-fill */}
      <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={styles.filterLabel}>Product Code</span>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search product code..."
            style={{ width: 200, height: 30, fontSize: 12 }}
            value={pfProductCodeFilter}
            onChange={(e) => {
              const val = e.target.value;
              setPfProductCodeFilter(val);
              if (val) {
                const match = productFormatList.find(p => (p.productCode || "").toLowerCase() === val.toLowerCase());
                if (match) setPfProductDescFilter(match.product_description || "");
              } else { setPfProductDescFilter(""); }
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 240 }}>
          <span style={styles.filterLabel}>Product Description</span>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search product description..."
            style={{ height: 30, fontSize: 12 }}
            value={pfProductDescFilter}
            onChange={(e) => {
              const val = e.target.value;
              setPfProductDescFilter(val);
              if (val) {
                const match = productFormatList.find(p => (p.product_description || "").toLowerCase() === val.toLowerCase());
                if (match) setPfProductCodeFilter(match.productCode || "");
              } else { setPfProductCodeFilter(""); }
            }}
          />
        </div>
        {(pfProductCodeFilter || pfProductDescFilter) && (
          <button style={styles.clearBtn} onClick={() => { setPfProductCodeFilter(""); setPfProductDescFilter(""); }}>× Clear</button>
        )}
      </div>
    </div>

    {/* Table — scrollable, compact */}
    {(() => {
      const filtered = productFormatList.filter((item) => {
        // cascading dropdown filters — match directly on product row fields
        for (const { key } of filterFields) {
          const sel = pfFilterSelections[key];
          if (!sel || sel.length === 0) continue;
          const rowVal = String(item[key] || "");
          if (!sel.some(o => o.value === rowVal)) return false;
        }
        if (pfProductCodeFilter.trim() && !(item.productCode || "").toLowerCase().includes(pfProductCodeFilter.trim().toLowerCase())) return false;
        if (pfProductDescFilter.trim() && !(item.product_description || "").toLowerCase().includes(pfProductDescFilter.trim().toLowerCase())) return false;
        return true;
      });

      const tdSm = { ...styles.erpTd, fontSize: 11, padding: "7px 8px" };

      const totalItems = filtered.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / pfPageSize));
      const safePage = Math.min(pfCurrentPage, totalPages);
      const startIdx = (safePage - 1) * pfPageSize;
      const pageItems = filtered.slice(startIdx, startIdx + pfPageSize);

      return (
        <>
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ ...styles.erpTable, fontSize: 12, whiteSpace: "nowrap" }}>
              <thead>
                <tr>
                  <th style={styles.erpTh}>GTP No.</th>
                  <th style={styles.erpTh}>Product Code</th>
                  <th style={styles.erpTh}>Product Description</th>
                  <th style={styles.erpTh}>Category</th>
                  <th style={styles.erpTh}>Cable Type</th>
                  <th style={styles.erpTh}>Code</th>
                  <th style={styles.erpTh}>Color</th>
                  <th style={styles.erpTh}>Cores</th>
                  <th style={styles.erpTh}>Cross Section</th>
                  <th style={styles.erpTh}>Std. Ref</th>
                  <th style={styles.erpTh}>Voltage</th>
                  <th style={styles.erpTh}>Construction</th>
                  <th style={styles.erpTh}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length > 0 ? pageItems.map((item, idx) => (
                  <tr key={`${item.gtp_id}-${item.productCode}-${idx}`}>
                    <td style={tdSm}>{item.gtp_number}</td>
                    <td style={tdSm}>{item.productCode || "—"}</td>
                    <td style={{ ...tdSm, whiteSpace: "normal", minWidth: 200 }}>{item.product_description || "—"}</td>
                    <td style={tdSm}>{item.category || "—"}</td>
                    <td style={tdSm}>{item.cableType || "—"}</td>
                    <td style={tdSm}>{item.code || "—"}</td>
                    <td style={tdSm}>{item.colour || "—"}</td>
                    <td style={tdSm}>{item.noOfCores || "—"}</td>
                    <td style={tdSm}>{item.nominalCrossSection || "—"}</td>
                    <td style={tdSm}>{item.referenceOfStandard || "—"}</td>
                    <td style={tdSm}>{item.voltage || "—"}</td>
                    <td style={tdSm}>{item.construction || "—"}</td>
                    <td style={{ ...tdSm, whiteSpace: "nowrap" }}>
                      <button className="btn btn-outline-dark btn-sm me-1" onClick={() => handleView(item.gtp_number, "product", item.gtp_id, item.productCode)}>View</button>
                      <button style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleDeleteProductFormat(item.gtp_number, item.productCode)} title="Delete">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="13" className="text-center text-muted p-3">No records found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div style={styles.paginationBar}>
            <div style={styles.paginationInfo}>
              {totalItems > 0
                ? `Showing ${startIdx + 1}-${Math.min(startIdx + pfPageSize, totalItems)} of ${totalItems}`
                : "Showing 0 of 0"}
            </div>
            <div style={styles.paginationControls}>
              {/* <label style={styles.paginationInfo}>
                Rows:&nbsp;
                <select
                  value={pfPageSize}
                  onChange={(e) => { setPfPageSize(Number(e.target.value)); setPfCurrentPage(1); }}
                  style={styles.paginationPageSizeSelect}
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label> */}
              <button
                style={{ ...styles.paginationBtn, ...(safePage <= 1 ? styles.paginationBtnDisabled : {}) }}
                disabled={safePage <= 1}
                onClick={() => setPfCurrentPage(safePage - 1)}
              >Prev</button>
              <span style={styles.paginationInfo}>Page {safePage} of {totalPages}</span>
              <button
                style={{ ...styles.paginationBtn, ...(safePage >= totalPages ? styles.paginationBtnDisabled : {}) }}
                disabled={safePage >= totalPages}
                onClick={() => setPfCurrentPage(safePage + 1)}
              >Next</button>
            </div>
          </div>
        </>
      );
    })()}
  </>
)}
      </div>
      {showAssignModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: 860, height: "auto", maxHeight: "90vh" }}>
            <div style={styles.modalHeader}>
              <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>Assign GTP to Products</h5>
              <button style={styles.closeBtn} onClick={() => { setShowAssignModal(false); setAssignGtpSelected(null); setAssignGtpCharacteristics([]); setAssignStdValues({}); }}>✖</button>
            </div>
            <div style={{ ...styles.modalBody, padding: "24px 28px", overflowY: "auto" }}>
              {/* Applied filters summary */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Applied Filters</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    ...filterFields.map(({ key, label }) => [label, prodFilterSelections[key]?.map(o => o.value).join(", ")]).filter(([, v]) => v),
                  ].map(([label, value]) => (
                    <div key={label} style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                      <span style={{ color: "#64748b", fontWeight: 600 }}>{label}: </span>
                      <span style={{ color: "#1e293b" }}>{value}</span>
                    </div>
                  ))}
                </div>
                {assignMatchCount !== null && (
                  <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: assignMatchCount > 0 ? "#16a34a" : "#dc2626" }}>
                    {assignMatchCount > 0 ? `${assignMatchCount} product${assignMatchCount !== 1 ? "s" : ""} will be assigned` : "No products match the current filters"}
                  </div>
                )}
              </div>

              {/* GTP Number selector */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ ...styles.compactLabel, fontSize: 13 }}>Select GTP Number <span style={{ color: "#dc2626" }}>*</span></label>
                <Select
                  options={gtpList.map(g => ({ value: g.gtp_number, label: `${g.gtp_number} — ${g.product_type || ""}` }))}
                  value={assignGtpSelected}
                  onChange={handleAssignGtpSelect}
                  placeholder="Search and select a GTP..."
                  isClearable
                  styles={customSelectStyles}
                />
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Select a GTP number to assign to the matched products.</div>
              </div>

              {/* Parameters table — shown after GTP is selected */}
              {assignGtpCharacteristics.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                    Standard Values <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(will apply to all matched products)</span>
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={{ ...styles.erpTh, width: "35%" }}>Process</th>
                          <th style={{ ...styles.erpTh, width: "35%" }}>Parameter</th>
                          <th style={{ ...styles.erpTh, width: "15%" }}>UOM</th>
                          <th style={{ ...styles.erpTh, width: "15%" }}>Std Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignGtpCharacteristics.map((c, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.processName || "—"}</td>
                            <td style={{ ...styles.erpTd, fontSize: 13 }}>{c.characteristic}</td>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.uom || "—"}</td>
                            <td style={{ ...styles.erpTd, padding: "6px 10px" }}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder=""
                                value={assignStdValues[`${c.processName}||${c.characteristic}`] || ""}
                                onChange={(e) => setAssignStdValues(prev => ({ ...prev, [`${c.processName}||${c.characteristic}`]: e.target.value }))}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div style={{ ...styles.modalFooter, gap: 10 }}>
              <button style={{ ...styles.saveBtn, background: "#64748b" }} onClick={() => { setShowAssignModal(false); setAssignGtpSelected(null); setAssignGtpCharacteristics([]); setAssignStdValues({}); }}>Cancel</button>
              <button
                style={{ ...styles.saveBtn, opacity: (!assignGtpSelected || assignSaving) ? 0.6 : 1, cursor: (!assignGtpSelected || assignSaving) ? "not-allowed" : "pointer" }}
                disabled={!assignGtpSelected || assignSaving}
                onClick={handleAssignGtp}
              >
                {assignSaving ? "Saving..." : "Assign "}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ VIEW / EDIT ASSIGNMENT MODAL ═══════════ */}
      {showEditAssignModal && editAssignRow && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: 860, height: "auto", maxHeight: "92vh" }}>

            {/* Header */}
            <div style={styles.modalHeader}>
              <div>
                <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>
                  {editAssignMode === "edit" ? "Edit Assignment" : "View Assignment"}
                  {" "}
                  <span style={{ color: "#021843" }}>{editAssignRow.gtp_number}</span>
                </h5>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {editAssignMode === "edit"
                    ? `Standard Value, GTP Type & Master Doc changes will propagate to all ${editAssignRow.product_count} linked product(s)`
                    : `${editAssignRow.product_count} product(s) currently linked`}
                </div>
              </div>
              <button style={styles.closeBtn} onClick={() => { setShowEditAssignModal(false); setEditAssignRow(null); setEditAssignChars([]); setEditAssignStdValues({}); setEditAssignGtpType({}); setEditAssignMasterDoc({}); }}>✖</button>
            </div>

            {/* Body */}
            <div style={{ ...styles.modalBody, padding: "24px 28px", overflowY: "auto" }}>

              {/* Criteria summary */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Assignment Criteria</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    ["Category",        editAssignRow.category],
                    ["Cable Type",      editAssignRow.cableType],
                    ["Code",            editAssignRow.code],
                    ["Colour",          editAssignRow.colour],
                    ["No of Cores",     editAssignRow.noOfCores],
                    ["Cross Section",   editAssignRow.nominalCrossSection],
                    ["Std Reference",   editAssignRow.referenceOfStandard],
                    ["Voltage",         editAssignRow.voltage],
                    ["Construction",    editAssignRow.construction],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                      <span style={{ color: "#64748b", fontWeight: 600 }}>{label}: </span>
                      <span style={{ color: "#1e293b" }}>{value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: "#0369a1" }}>
                  {editAssignRow.product_count} product(s) currently linked
                </div>
              </div>

              {/* Parameters table */}
              {editAssignChars.length > 0 ? (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                    Standard Values
                    {editAssignMode === "edit" && (
                      <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8", marginLeft: 8 }}>
                        (changes will apply to all linked products)
                      </span>
                    )}
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={{ ...styles.erpTh, width: "18%" }}>Process</th>
                          <th style={{ ...styles.erpTh, width: "24%" }}>Parameter</th>
                          <th style={{ ...styles.erpTh, width: "7%" }}>UOM</th>
                          <th style={{ ...styles.erpTh, width: "15%" }}>GTP Type</th>
                          <th style={{ ...styles.erpTh, width: "12%", textAlign: "center" }}>Master Doc</th>
                          <th style={{ ...styles.erpTh, width: "24%" }}>Standard Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editAssignChars.map((c, i) => {
                          const key = `${c.processName}||${c.characteristic}`;
                          const typesSet = editAssignGtpType[key] || new Set();
                          const isMasterDoc = !!editAssignMasterDoc[key];
                          const isEditable = editAssignMode === "edit";

                          const toggleGtpType = (type) => {
                            setEditAssignGtpType(prev => {
                              const next = new Set(prev[key] || []);
                              if (next.has(type)) next.delete(type); else next.add(type);
                              return { ...prev, [key]: next };
                            });
                          };

                          return (
                          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.processName || "—"}</td>
                            <td style={{ ...styles.erpTd, fontSize: 13 }}>{c.characteristic}</td>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.uom || "—"}</td>
                            <td style={{ ...styles.erpTd, fontSize: 12 }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: isEditable ? "pointer" : "default" }}>
                                  <input
                                    type="checkbox"
                                    checked={typesSet.has("Sales")}
                                    disabled={!isEditable}
                                    onChange={() => toggleGtpType("Sales")}
                                    style={{ margin: 0 }}
                                  />
                                  <span>Sales</span>
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: isEditable ? "pointer" : "default" }}>
                                  <input
                                    type="checkbox"
                                    checked={typesSet.has("Master")}
                                    disabled={!isEditable}
                                    onChange={() => toggleGtpType("Master")}
                                    style={{ margin: 0 }}
                                  />
                                  <span>Master</span>
                                </label>
                              </div>
                            </td>
                            <td style={{ ...styles.erpTd, fontSize: 12, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={isMasterDoc}
                                disabled={!isEditable}
                                onChange={(e) => setEditAssignMasterDoc(prev => ({ ...prev, [key]: e.target.checked }))}
                                style={{ margin: 0, cursor: isEditable ? "pointer" : "default" }}
                              />
                            </td>
                            <td style={{ ...styles.erpTd, padding: "6px 10px" }}>
                              {isEditable ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder=""
                                  value={editAssignStdValues[key] ?? ""}
                                  onChange={(e) => setEditAssignStdValues(prev => ({ ...prev, [key]: e.target.value }))}
                                />
                              ) : (
                                <span style={{ fontSize: 13, color: "#1e293b" }}>
                                  {editAssignStdValues[key] || ""}
                                </span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "30px 0", fontSize: 14 }}>
                  Loading characteristics...
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ ...styles.modalFooter, gap: 10 }}>
              <button
                style={{ ...styles.saveBtn, background: "#64748b" }}
                onClick={() => { setShowEditAssignModal(false); setEditAssignRow(null); setEditAssignChars([]); setEditAssignStdValues({}); setEditAssignGtpType({}); setEditAssignMasterDoc({}); setEditAssignMode("view"); }}
              >
                Close
              </button>
              {editAssignMode === "view" ? (
                <button
                  style={styles.saveBtn}
                  onClick={() => setEditAssignMode("edit")}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    style={{ ...styles.saveBtn, background: "#64748b" }}
                    onClick={() => setEditAssignMode("view")}
                  >
                    Cancel
                  </button>
                  <button
                    style={{ ...styles.saveBtn, opacity: editAssignSaving ? 0.6 : 1, cursor: editAssignSaving ? "not-allowed" : "pointer" }}
                    disabled={editAssignSaving}
                    onClick={handleSaveEditAssign}
                  >
                    {editAssignSaving ? "Saving..." : `Update ${editAssignRow.product_count} Product(s)`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ GTP CONFIGURATION MODAL ═══════════ */}
      {showModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h4>GTP Configuration</h4>
              <button style={styles.closeBtn} onClick={() => {
                setShowModal(false); setActiveViewTab("particulars");
                setPdfPreviewUrl(null); setShowDatasheetPreview(false);
                resetForm(); setSelectedCharacteristics({}); setSelectedGtpId(null);
                setIsStdValueEditMode(false);
              }}>✖</button>
            </div>
            <div style={styles.modalBody}>
              {/* GTP Number */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label style={styles.compactLabel}>GTP Number</label>
                  <input type="text" className="form-control" value={formData.gtpNumber} disabled={isViewMode}
                    onChange={(e) => setFormData({ ...formData, gtpNumber: e.target.value })} placeholder="e.g. GTP/2024/001" />
                </div>
                {(isEditMode || isViewMode) && viewContext === "product" && (
                  <div className="col-md-3">
                    <label style={styles.compactLabel}>Checked By</label>
                    <input
  type="text"
  className="form-control"
  value={capitalizeFirst(checkedBy)}
  readOnly
  placeholder="Enter name"
/>
                  </div>
                )}
              </div>

              {/* Summary fields */}
              <div style={styles.selectedFormSection}>
                <div className="row">
                  {Object.entries({
                    "GTP Number": formData.gtpNumber,
                    ...(viewContext === "product" ? { "Product Code": formData.productCode, "Product Description": formData.productDescription } : {}),
                    "Category": formData.category,
                    "Cable Type": formData.cableType,
                    "Construction": formData.construction,
                    "Code": formData.code,
                    "No Of Cores": formData.noOfCores,
                    "Nominal Cross Section": formData.nominalCrossSection,
                    "Colour": formData.colour,
                    "Voltage": formData.voltage,
                    "Reference Of Standard": formData.referenceOfStandard,
                  }).filter(([, value]) => value).map(([label, value], index) => (
                    <div className="col-md-4 mb-2" key={index}>
                      <div style={styles.simpleFieldRow}>
                        <label style={styles.simpleFieldLabel}>{label}</label>
                        <input type="text" value={value} readOnly className="form-control form-control-sm" style={styles.simpleFieldInput} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* VIEW MODE */}
              {isViewMode && (
                <div>
                  <div style={styles.tabs}>
                    {["particulars", "docs"].map((tab) => (
                      <button key={tab} className={`btn ${activeViewTab === tab ? "btn-secondary" : "btn-light"}`}
                        onClick={() => setActiveViewTab(tab)} style={styles.compactTabBtn}>
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                  {activeViewTab === "docs" && (
                    <div style={{ padding: "16px 0" }}>
                      <div style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "16px", marginBottom: "12px" }}>
                        <strong>{formData.gtpNumber || "GTP"}_Datasheet</strong>
                        <div style={{ marginTop: "10px" }}>
                          <button className="btn btn-dark btn-sm me-2" onClick={handleViewPdf}>View</button>
                          <button className="btn btn-dark btn-sm me-2" onClick={handleDownloadPdf}>Download</button>
                          <button className="btn btn-dark btn-sm" onClick={handlePrintPdf}>Print</button>
                        </div>
                      </div>
                      <div style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "16px", marginBottom: "12px" }}>
                        <strong>{formData.gtpNumber || "GTP"}_SalesDocs</strong>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Characteristics with GTP Type "Sales" selected</div>
                        <div style={{ marginTop: "10px" }}>
                          <button className="btn btn-dark btn-sm me-2" onClick={handleViewSalesPdf}>View</button>
                          <button className="btn btn-dark btn-sm me-2" onClick={handleDownloadSalesPdf}>Download</button>
                          <button className="btn btn-dark btn-sm" onClick={handlePrintSalesPdf}>Print</button>
                        </div>
                      </div>
                      <div style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "16px", marginBottom: "12px" }}>
                        <strong>{formData.gtpNumber || "GTP"}_MasterDocs</strong>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Characteristics with the "Master Doc" checkbox ticked</div>
                        <div style={{ marginTop: "10px" }}>
                          <button className="btn btn-dark btn-sm me-2" onClick={handleViewMasterPdf}>View</button>
                          <button className="btn btn-dark btn-sm me-2" onClick={handleDownloadMasterPdf}>Download</button>
                          <button className="btn btn-dark btn-sm" onClick={handlePrintMasterPdf}>Print</button>
                        </div>
                      </div>
                      {showDatasheetPreview && (
                        <div style={styles.datasheetOverlay}>
                          <div style={styles.datasheetPopup}>
                            <div style={styles.datasheetPopupHeader}>
                              <strong>{formData.gtpNumber || "GTP"}_{previewDocLabel}.pdf</strong>
                              <button style={styles.closeBtn} onClick={() => setShowDatasheetPreview(false)}>✖</button>
                            </div>
                            <div style={styles.datasheetPopupBody}>
                              <div style={{ display: "flex", alignItems: "center", marginBottom: "8px", position: "relative" }}>
                                <img src={sbeeLogo} alt="SBEE" style={{ height: "48px", position: "absolute", left: 0 }} />
                                <div style={{ flex: 1, textAlign: "center" }}>
                                  <div style={{ fontSize: "15px", fontWeight: 700 }}>SBEE CABLES INDIA LTD</div>
                                  <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", marginTop: "2px" }}>
                                    {`${previewTitlePrefix} ${formData.productType || "FINISHED GOODS"}`.toUpperCase()}
                                  </div>
                                </div>
                              </div>
                             <div
  style={{
    fontSize: "11px",
    border: "1px solid #333",
    borderBottom: "none",
    padding: "5px 10px",
    background: "#fff",
    textAlign: "center",
    fontWeight: "500"
  }}
>
  <strong>Product Code:</strong> {formData.productCode || "-"}
  {"  |  "}
  <strong>Description:</strong> {formData.productDescription || "-"}
</div>
                              <table style={{ ...styles.datasheetTable, marginTop: 0 }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...styles.datasheetTh, width: "48px", textAlign: "center" }}>S.No</th>
                                    <th style={styles.datasheetTh}>Parameters</th>
                                    <th style={{ ...styles.datasheetTh, width: "70px", textAlign: "center" }}>UOM</th>
                                    <th style={{ ...styles.datasheetTh, textAlign: "center" }}>Specified Values</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td style={{ ...styles.datasheetTd, textAlign: "center", fontWeight: 700 }}>1</td>
                                    <td style={{ ...styles.datasheetTd, fontWeight: 700 }}>GENERAL</td>
                                    <td style={styles.datasheetTd}></td>
                                    <td style={styles.datasheetTd}></td>
                                  </tr>
                                  <tr>
                                    <td style={{ ...styles.datasheetTd, textAlign: "center" }}>1.1</td>
                                    <td style={styles.datasheetTd}>Name &amp; Address of the manufacturer</td>
                                    <td style={{ ...styles.datasheetTd, textAlign: "center" }}></td>
                                    <td style={{ ...styles.datasheetTd, textAlign: "center" }}>{manufacturerAddress}</td>
                                  </tr>
                                  <tr>
                                    <td style={{ ...styles.datasheetTd, textAlign: "center" }}>1.2</td>
                                    <td style={styles.datasheetTd}>Trade mark / Brand Name</td>
                                    <td style={{ ...styles.datasheetTd, textAlign: "center" }}></td>
                                    <td style={{ ...styles.datasheetTd, textAlign: "center" }}><img src={sbeeLogo} alt="SBEE" style={{ height: "32px" }} /></td>
                                  </tr>
                                  {(() => {
                                    let lastH = null, sec = 1, sub = 0;
                                    return previewCharacteristics.flatMap(([, val], idx) => {
                                      const rows = [];
                                      const hdr = val.processName || "";
                                      if (hdr !== lastH) {
                                        sec++; sub = 0; lastH = hdr;
                                        rows.push(<tr key={`h-${idx}`}><td style={{ ...styles.datasheetTd, textAlign: "center", fontWeight: 700 }}>{sec}</td><td style={{ ...styles.datasheetTd, fontWeight: 700 }}>{hdr.toUpperCase()}</td><td style={styles.datasheetTd}></td><td style={styles.datasheetTd}></td></tr>);
                                      }
                                      sub++;
                                      rows.push(<tr key={`r-${idx}`}><td style={{ ...styles.datasheetTd, textAlign: "center" }}>{sec}.{sub}</td><td style={styles.datasheetTd}>{val.characteristic}</td><td style={{ ...styles.datasheetTd, textAlign: "center" }}>{val.uom}</td><td style={{ ...styles.datasheetTd, textAlign: "center" }}>{val.standardValue}</td></tr>);
                                      return rows;
                                    });
                                  })()}
                                </tbody>
                              </table>
                              <div style={styles.datasheetFooter}>
                                <div>Prepared By: <span style={{ ...styles.datasheetSignLine, paddingLeft: 4 }}>{capitalizeFirst(checkedBy)}</span></div>
                            
                                <div>Approved By: <span style={styles.datasheetSignLine} /></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {activeViewTab === "particulars" && (
                    <table style={styles.characteristicsTable}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.tableHeader, width: "50px" }}>Order</th>
                          <th style={styles.tableHeader}>Process</th>
                          <th style={styles.tableHeader}>Sub Process</th>
                          <th style={styles.tableHeader}>Characteristic</th>
                          <th style={styles.tableHeader}>UOM</th>
                          <th style={styles.tableHeader}>Source</th>
                          <th style={styles.tableHeader}>GTP Type</th>
                          <th style={{ ...styles.tableHeader, textAlign: "center" }}>
                            {isStdValueEditMode ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                <span>Master Doc</span>
                                <label style={{ display: "flex", alignItems: "center", gap: 4, fontWeight: 400, fontSize: 11, cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    title="Select All / Deselect All"
                                    checked={viewCharacteristics.length > 0 && viewCharacteristics.every(([, v]) => !!v.masterDoc)}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setSelectedCharacteristics((prev) => {
                                        const updated = { ...prev };
                                        viewCharacteristics.forEach(([k]) => {
                                          if (updated[k]) updated[k] = { ...updated[k], masterDoc: checked };
                                        });
                                        return updated;
                                      });
                                    }}
                                  />
                                  <span>All</span>
                                </label>
                              </div>
                            ) : "Master Doc"}
                          </th>
                          {viewContext === "product" && <th style={styles.tableHeader}>Standard Value</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {viewCharacteristics.map(([key, val]) => (
                          <tr key={key}>
                            <td style={{ ...styles.tableCell, textAlign: "center" }}>{val.order || "-"}</td>
                            <td style={styles.tableCell}>{val.processName}</td>
                            <td style={styles.tableCell}>{val.subProcessName}</td>
                            <td style={styles.tableCell}>{val.characteristic}</td>
                            <td style={styles.tableCellUom}>{val.uom}</td>
                            <td style={styles.tableCell}>{val.source}</td>
                            <td style={{ ...styles.tableCell, minWidth: 150 }}>
                              {isStdValueEditMode ? (
                                <Select
                                  isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                                  options={GTP_TYPE_OPTIONS}
                                  value={val.gtpType || []}
                                  onChange={(opts) => handleGtpTypeChange(key, opts)}
                                  placeholder="GTP Type"
                                  components={{ Option: CheckboxOption }}
                                  menuPortalTarget={document.body} menuPosition="fixed"
                                  styles={filterSelectStyles}
                                />
                              ) : (
                                (val.gtpType || []).map((o) => o.label).join(", ")
                              )}
                            </td>
                            <td style={{ ...styles.tableCell, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={!!val.masterDoc}
                                disabled={!isStdValueEditMode}
                                onChange={(e) => handleMasterDocChange(key, e.target.checked)}
                              />
                            </td>
                            {viewContext === "product" && (
                              <td style={styles.tableCell}>
                                {isStdValueEditMode ? (
                                  <input type="text" className="form-control form-control-sm" value={val.standardValue || ""}
                                    onChange={(e) => setSelectedCharacteristics((prev) => ({ ...prev, [key]: { ...prev[key], standardValue: e.target.value } }))} />
                                ) : (
                                  <span style={{ fontSize: 13, color: "#1e293b" }}>{val.standardValue || ""}</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* EDIT / ADD mode */}
              {(isEditMode || (!isViewMode && !isEditMode)) && qualityConfigurations.length > 0 && (
                <div>
                  {qualityConfigurations.map((group) => {
                    const groupKey = `GROUP__${group.process_name}`;
                    const isGroupSelected = !!selectedCharacteristics[groupKey];
                    const characteristics = group.characteristics || [];
                    return (
                      <div key={groupKey} style={styles.simpleGroupWrapper}>
                        <div style={styles.groupRow}>
                          <select
                            value={selectedCharacteristics[groupKey]?.order ?? ""}
                            onChange={(e) => handleGroupOrderChange(group.process_name, e.target.value)}
                            style={styles.groupOrderInput}
                          >
                            <option value="">-</option>
                            {Array.from({ length: qualityConfigurations.length }, (_, i) => i + 1).map((num) => {
                              const takenByOther = Object.keys(selectedCharacteristics).some((k) => {
                                if (!k.startsWith("GROUP__") || k === groupKey) return false;
                                const otherProcessName = k.slice("GROUP__".length).trim().toLowerCase();
                                if (!masterProcessSet.has(otherProcessName)) return false; // stale, ignore
                                return String(selectedCharacteristics[k]?.order) === String(num);
                              });
                              // Not disabled anymore — picking a taken number swaps orders
                              // with whichever process currently holds it (see handleGroupOrderChange).
                              return (
                                <option key={num} value={num}>
                                  {num}{takenByOther ? " (in use — picking swaps)" : ""}
                                </option>
                              );
                            })}
                          </select>
                          <label style={styles.groupLabel}>
                            <input type="checkbox" checked={isGroupSelected} onChange={() => handleProductGroupSelect(`${group.process_name}`, characteristics)} />
                            <div style={styles.processText}>{group.process_id} - {group.process_name}</div>
                          </label>
                        </div>
                        {isGroupSelected && (
                          <div style={styles.simpleCharacteristicsSection}>
                            <table style={styles.characteristicsTable}>
                              <thead>
                                <tr>
                                  <th style={{ ...styles.tableHeader, width: "50px" }}>
                                    <input type="checkbox"
                                      checked={characteristics.length > 0 && characteristics.every((item) => !!selectedCharacteristics[charKey(item.process_name, item.characteristic)])}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setSelectedCharacteristics((prev) => {
                                          const updated = { ...prev };
                                          characteristics.forEach((item) => {
                                            const key = charKey(item.process_name, item.characteristic);
                                            if (checked) updated[key] = updated[key] || { processId: item.process_id, processName: item.process_name, subProcessId: item.sub_process_id, subProcessName: item.sub_process_name, characteristic: item.characteristic, uom: item.uom || "", source: "", type: "", standardValue: "", gtpType: [], masterDoc: false };
                                            else delete updated[key];
                                          });
                                          return updated;
                                        });
                                      }}
                                    />
                                  </th>
                                  <th style={styles.tableHeader}>Parameters</th>
                                  <th style={styles.tableHeader}>Source</th>
                                  <th style={styles.tableHeader}>GTP Type</th>
                                  <th style={{ ...styles.tableHeader, textAlign: "center" }}>
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                      <span>Master Doc</span>
                                      <label style={{ display: "flex", alignItems: "center", gap: 4, fontWeight: 400, fontSize: 11, cursor: "pointer" }}>
                                        <input
                                          type="checkbox"
                                          title="Select All / Deselect All"
                                          checked={
                                            characteristics.some((item) => !!selectedCharacteristics[charKey(item.process_name, item.characteristic)]) &&
                                            characteristics
                                              .filter((item) => !!selectedCharacteristics[charKey(item.process_name, item.characteristic)])
                                              .every((item) => !!selectedCharacteristics[charKey(item.process_name, item.characteristic)]?.masterDoc)
                                          }
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setSelectedCharacteristics((prev) => {
                                              const updated = { ...prev };
                                              characteristics.forEach((item) => {
                                                const key = charKey(item.process_name, item.characteristic);
                                                if (updated[key]) updated[key] = { ...updated[key], masterDoc: checked };
                                              });
                                              return updated;
                                            });
                                          }}
                                        />
                                        <span>All</span>
                                      </label>
                                    </div>
                                  </th>
                                  <th style={styles.tableHeader}>UOM</th>
                                  <th style={styles.tableHeader}>Type</th>
                                  {viewContext === "product" && <th style={styles.tableHeader}>Standard Value</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {characteristics.map((item, index) => {
                                  const key = charKey(item.process_name, item.characteristic);
                                  const selected = selectedCharacteristics[key];
                                  return (
                                    <tr key={index}>
                                      <td style={styles.tableCellCheckbox}><input type="checkbox" checked={!!selected} onChange={() => handleCharacteristicSelect(group, item)} /></td>
                                      <td style={styles.tableCell}>{item.characteristic}</td>
                                      <td style={styles.tableCell}>
                                        <select className="form-select form-select-sm" disabled={!selected} value={selected?.source || ""} onChange={(e) => handleSourceChange(key, item.characteristic, e.target.value, item.process_name)}>
                                          <option value="">Select Source</option>
                                          {SOURCE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                      </td>
                                      <td style={{ ...styles.tableCell, minWidth: 150 }}>
                                        <Select
                                          isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                                          isDisabled={!selected}
                                          options={GTP_TYPE_OPTIONS}
                                          value={selected?.gtpType || []}
                                          onChange={(opts) => handleGtpTypeChange(key, opts)}
                                          placeholder="GTP Type"
                                          components={{ Option: CheckboxOption }}
                                          menuPortalTarget={document.body} menuPosition="fixed"
                                          styles={filterSelectStyles}
                                        />
                                      </td>
                                      <td style={{ ...styles.tableCell, textAlign: "center" }}>
                                        <input
                                          type="checkbox"
                                          disabled={!selected}
                                          checked={!!selected?.masterDoc}
                                          onChange={(e) => handleMasterDocChange(key, e.target.checked)}
                                        />
                                      </td>
                                      <td style={styles.tableCellUom}>{item.uom || "-"}</td>
                                      <td style={styles.tableCell}>
                                        <select className="form-select form-select-sm" disabled={!selected} value={selected?.type || ""} onChange={(e) => setSelectedCharacteristics((prev) => ({ ...prev, [key]: { ...prev[key], type: e.target.value } }))}>
                                          <option value="">Select</option>
                                          <option value="Text">Text</option>
                                          <option value="Integer">Integer</option>
                                          <option value="Range">Range</option>
                                          <option value="Min-Max">Min-Max</option>
                                        </select>
                                      </td>
                                      {viewContext === "product" && (
                                        <td style={styles.tableCell}>
                                          <input type="text" className="form-control form-control-sm" disabled={!selected} placeholder="Standard Value"
                                            value={selected?.standardValue || ""} onChange={(e) => setSelectedCharacteristics((prev) => ({ ...prev, [key]: { ...prev[key], standardValue: e.target.value } }))} />
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {(isEditMode || (!isViewMode && !isEditMode)) && qualityConfigurations.length === 0 && (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "40px 0", fontSize: 14 }}>
                  {formData.productType ? "Loading parameters..." : "Loading parameters..."}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              {/* Create / Edit GTP mode */}
              {!isViewMode && (
                <button style={styles.saveBtn} onClick={isEditMode ? handleUpdate : handleSave}>
                  {isEditMode ? "Update" : "Save"}
                </button>
              )}
              {/* View mode — product context: show Edit → Save/Cancel flow */}
              {isViewMode && viewContext === "product" && (
                isStdValueEditMode ? (
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      style={{ ...styles.saveBtn, background: "#64748b" }}
                      onClick={() => setIsStdValueEditMode(false)}
                    >
                      Cancel
                    </button>
                    <button style={styles.saveBtn} onClick={handleProductStdValueSave}>
                      Save
                    </button>
                  </div>
                ) : (
                  <button style={styles.saveBtn} onClick={() => setIsStdValueEditMode(true)}>
                    Edit
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />
    </Layout>
  );
}

/* ================= STYLES ================= */
const styles = {
  pageTitleBar:   { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 },
  pageTitle:      { fontSize:30, fontWeight:700, color:"#1e293b", marginBottom:2 },
  pageSubtitle:   { fontSize:12, color:"#64748b" },
  filterCard:     { background:"#ffffff", borderRadius:"12px", padding:"18px", boxShadow:"0 2px 8px rgba(15,23,42,0.06)", border:"1px solid #e2e8f0", marginBottom:"18px" },
  compactLabel:   { fontSize:"12px", fontWeight:600, marginBottom:"4px", color:"#334155", display:"block" },
  filterBar:      { display:"flex", alignItems:"flex-end", gap:8, flexWrap:"nowrap", overflowX:"auto", padding:"4px 0" },
  filterLabel:    { fontSize:11, fontWeight:600, color:"#64748b", whiteSpace:"nowrap", letterSpacing:"0.02em", paddingLeft:2 },
  clearBtn:       { border:"1px solid #cbd5e1", borderRadius:4, background:"#fff", fontSize:11, color:"#64748b", padding:"0 10px", height:30, cursor:"pointer", whiteSpace:"nowrap" },
  matchHint:      { marginTop:8, fontSize:12, color:"#475569" },
  tableContainer: { background:"#ffffff", borderRadius:8, padding:10, boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  toggleBar:      { display:"flex", gap:"0px", marginBottom:"12px", borderBottom:"1px solid #e2e8f0", paddingBottom:"10px" },
  toggleBtn:      { border:"none", borderRadius:"6px", padding:"6px 20px", fontSize:"13px", fontWeight:600, cursor:"pointer", transition:"all 0.15s" },
  toggleBtnActive:   { background:"#021843", color:"#ffffff" },
  toggleBtnInactive: { background:"#f1f5f9", color:"#64748b" },
  erpTable:       { width:"100%", borderCollapse:"collapse" },
  erpTh:          { background:"#f8fafc", fontSize:12, fontWeight:600, padding:"10px", borderBottom:"1px solid #e5e7eb", color:"#1e293b" },
  erpTd:          { fontSize:13, padding:"10px", borderBottom:"1px solid #f1f5f9", color:"#334155" },
  modalBackdrop:  { position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:9999, padding:"20px" },
  modal:          { width:"95%", maxWidth:"1280px", height:"92vh", background:"#ffffff", borderRadius:"16px", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 15px 40px rgba(0,0,0,0.18)" },
  modalHeader:    { padding:"18px 24px", borderBottom:"1px solid #e2e8f0", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#ffffff" },
  closeBtn:       { border:"none", background:"transparent", fontSize:"24px", color:"#64748b", cursor:"pointer" },
  modalBody:      { flex:1, overflowY:"auto", padding:"22px 24px", background:"#f8fafc" },
  modalFooter:    { padding:"16px 24px", borderTop:"1px solid #e2e8f0", display:"flex", justifyContent:"flex-end", background:"#ffffff" },
  selectedFormSection: { marginBottom:"18px" },
  simpleFieldRow:  { display:"flex", flexDirection:"column" },
  simpleFieldLabel: { fontSize:"11px", fontWeight:600, color:"#64748b", marginBottom:"4px", textTransform:"uppercase" },
  simpleFieldInput: { height:"32px", fontSize:"13px", background:"#fff" },
  saveBtn:         { background:"#021843", color:"#fff", border:"none", borderRadius:"8px", padding:"10px 26px", fontSize:"14px", fontWeight:600, cursor:"pointer" },
  characteristicsTable: { width:"100%", borderCollapse:"collapse", marginTop:"8px", background:"#fff" },
  tableHeader:     { fontSize:"13px", fontWeight:700, color:"#475569", textAlign:"left", padding:"10px 12px", borderBottom:"1px solid #e2e8f0", background:"#f8fafc" },
  tableCellCheckbox: { width:"50px", padding:"10px 12px", borderBottom:"1px solid #f1f5f9" },
  tableCell:       { fontSize:"14px", color:"#1e293b", padding:"10px 12px", borderBottom:"1px solid #f1f5f9" },
  tableCellUom:    { fontSize:"13px", color:"#64748b", padding:"10px 12px", borderBottom:"1px solid #f1f5f9", fontWeight:500 },
  simpleGroupWrapper: { marginBottom:"8px" },
  groupRow:        { padding:"4px 0", display:"flex", alignItems:"center", gap:"8px" },
  groupOrderInput: { width:"56px", padding:"2px 4px", fontSize:"13px", border:"1px solid #cbd5e1", borderRadius:"4px", textAlign:"center" },
  groupLabel:      { display:"flex", alignItems:"center", gap:"10px", cursor:"pointer", margin:0 },
  simpleCharacteristicsSection: { paddingLeft:"28px", paddingTop:"4px", paddingBottom:"8px" },
  processText:     { fontSize:"15px", fontWeight:700, color:"#1e293b" },
  tabs:            { display:"flex", gap:"8px", marginBottom:"12px", marginTop:"8px" },
  compactTabBtn:   { fontSize:"13px", padding:"4px 14px" },
  datasheetOverlay: { position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:10050, padding:"20px" },
  datasheetPopup:  { width:"95%", maxWidth:"950px", maxHeight:"90vh", background:"#ffffff", borderRadius:"10px", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 15px 40px rgba(0,0,0,0.25)" },
  datasheetPopupHeader: { padding:"12px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#f8fafc" },
  datasheetPopupBody: { flex:1, overflowY:"auto", padding:"24px", background:"#ffffff" },
  datasheetTable:  { width:"100%", borderCollapse:"collapse", marginTop:"0px", fontSize:"13px", border:"1px solid #333" },
  datasheetTh:     { border:"1px solid #333", padding:"7px 10px", background:"#dcdcdc", fontWeight:700, textAlign:"left" },
  datasheetTd:     { border:"1px solid #333", padding:"6px 10px", color:"#1e293b" },
  datasheetFooter: { display:"flex", justifyContent:"space-between", marginTop:"50px", fontSize:"13px" },
  datasheetSignLine: { display:"inline-block", borderBottom:"1px solid #333", width:"150px", marginLeft:"8px" },
  paginationBar:      { display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:"12px", flexWrap:"wrap", gap:8 },
  paginationInfo:     { fontSize:"13px", color:"#64748b" },
  paginationControls: { display:"flex", alignItems:"center", gap:"10px", marginLeft:"auto" },
  paginationPageSizeSelect: { fontSize:"13px", padding:"4px 6px", borderRadius:"4px", border:"1px solid #cbd5e1", background:"#fff", color:"#334155" },
  paginationBtn:      { fontSize:"13px", fontWeight:600, padding:"5px 14px", borderRadius:"4px", border:"1px solid #cbd5e1", background:"#fff", color:"#334155", cursor:"pointer" },
  paginationBtnDisabled: { opacity:0.5, cursor:"not-allowed" },
};

export default GTPConfiguration;